import { cpSync, mkdirSync, mkdtempSync, readFileSync, rmSync, writeFileSync } from 'node:fs';
import { tmpdir } from 'node:os';
import * as path from 'node:path';
import { Command, Option } from 'commander';
import {
  appOptions,
  commonOptions,
  contextOptions,
  regionOption,
  parseContextOptions,
  parseAssumeRoleToken,
  effectiveAssumeRoleArn,
  normalizeStartApiAssumeRole,
  type AssumeRoleOption,
} from '../options.js';
import { resolveProfileCredentials, buildStsClientConfig } from '../../utils/profile-resolver.js';
import { getLogger } from '../../utils/logger.js';
import { describeAwsFailureForWarn, flattenToOneLine } from '../../local/credential-error.js';
import {
  applyRoleArnIfSet,
  assumeRoleCredentials,
  describeRejectedRoleArn,
  isIamRoleArn,
} from '../../utils/role-arn.js';
import { withErrorHandling } from '../../utils/error-handler.js';
import { listTargets } from '../../local/target-lister.js';
import { isInteractive, pickManyTargets } from '../../local/target-picker.js';
import { Synthesizer, type SynthesisOptions } from '../../synthesis/synthesizer.js';
import { resolveApp, resolveWatchConfig, type CdkWatchConfig } from '../config-loader.js';
import { createGlobMatcher } from '../../utils/glob-match.js';
import { readCdkPathOrUndefined } from '../cdk-path.js';
import { resolveExecutionRoleArnFromState } from './local-invoke.js';
import {
  createLocalStateProvider,
  isCfnFlagPresent,
  rejectExplicitCfnStackWithMultipleStacks,
  resolveCfnFallbackRegion,
  type ExtraStateProviders,
} from './local-state-source.js';
import {
  getEmbedConfig,
  setEmbedConfig,
  type CdkLocalEmbedConfig,
} from '../../local/embed-config.js';
import type { StackInfo } from '../../synthesis/assembly-reader.js';
import type { TemplateResource } from '../../types/resource.js';
import type { StackState } from '../../types/state.js';
import {
  applyDeployedEnvFallback,
  substituteEnvVarsFromState,
  type PseudoParameters,
  type SubstitutionContext,
} from '../../local/state-resolver.js';
import { derivePartitionAndUrlSuffix } from '../../local/ecs-task-resolver.js';
import { resolveRuntimeFileExtension, resolveRuntimeImage } from '../../local/runtime-image.js';
import { ensureDockerAvailable, pullImage } from '../../local/docker-runner.js';
import { architectureToPlatform, buildContainerImage } from '../../local/docker-image-builder.js';
import { parseEcrUri, pullEcrImage } from '../../local/ecr-puller.js';
import {
  derivePseudoParametersFromRegion,
  tryResolveImageFnJoin,
} from '../../local/intrinsic-image.js';
import {
  AssetManifestLoader,
  getDockerImageBySourceHash,
} from '../../assets/asset-manifest-loader.js';
import type { DockerImageAssetSource } from '../../types/assets.js';
import { discoverRoutes, type DiscoveredRoute } from '../../local/route-discovery.js';
import {
  discoverWebSocketApis,
  filterWebSocketApisByIdentifiers,
  availableWebSocketApiIdentifiers,
  type DiscoveredWebSocketApi,
} from '../../local/websocket-route-discovery.js';
import {
  attachWebSocketServer,
  handleManagementRequest,
  type AttachedWebSocketServer,
} from '../../local/websocket-server.js';
import { buildMgmtEndpointEnvUrl } from '../../local/websocket-mgmt-api.js';
import { HOST_GATEWAY_MIN_VERSION, probeHostGatewaySupport } from '../../local/docker-version.js';
import { warnSsrfRiskyUri } from '../../local/rest-v1-integrations.js';
import {
  createContainerPool,
  type ContainerSpec,
  type ContainerPool,
} from '../../local/container-pool.js';
import {
  startApiServer,
  readMtlsMaterialsFromDisk,
  type ServerState,
  type StartedApiServer,
  type MtlsServerConfig,
} from '../../local/http-server.js';
import {
  availableApiIdentifiers,
  filterRoutesByApiIdentifiers,
  groupRoutesByServer,
  type ApiServerGroup,
} from '../../local/api-server-grouping.js';
import { resolveEnvVars, type EnvOverrideFile } from '../../local/env-resolver.js';
import {
  extractEphemeralStorageMb,
  materializeAssetCodeDir,
  resolveLambdaArchitecture,
  resolveLambdaLayers,
  type ResolvedLambdaLayer,
} from '../../local/lambda-resolver.js';
import { materializeLayerFromArn } from '../../local/layer-arn-materializer.js';
import { matchStacks } from '../stack-matcher.js';
import {
  buildCorsConfigByApiId,
  buildCorsConfigFromCloudFrontChain,
  type CorsConfig,
} from '../../local/cors-handler.js';
import {
  attachStageContext,
  buildStageMap,
  type ResolvedStage,
} from '../../local/stage-resolver.js';
import { createFileWatcher, type FileWatcher } from '../../local/file-watcher.js';
import { type NextStateMaterial } from '../../local/reload-orchestrator.js';
import {
  attachAuthorizers,
  type AuthorizerInfo,
  type RouteWithAuth,
} from '../../local/authorizer-resolver.js';
import { createAuthorizerCache } from '../../local/authorizer-cache.js';
import {
  buildCognitoJwksUrl,
  buildJwksUrlFromIssuer,
  createJwksCache,
  type WarnedAt,
} from '../../local/cognito-jwt.js';
import { defaultCredentialsLoader, type CredentialsLoader } from '../../local/sigv4-verify.js';
import { singleFlight } from '../../utils/single-flight.js';
import { formatAuthority } from '../../utils/url-authority.js';
import {
  writeProfileCredentialsFile,
  type ProfileCredentialsFile,
} from './local-profile-credentials-file.js';

interface LocalStartApiOptions {
  app?: string;
  output: string;
  verbose: boolean;
  region?: string;
  profile?: string;
  roleArn?: string;
  context?: string[];
  /** Bind port (default 0 = auto-allocate). */
  port: string;
  /** Bind host (default 127.0.0.1). */
  host: string;
  /** Stack pattern (single-stack apps auto-detect). */
  stack?: string;
  /**
   * Issue #55: serve the union of every stack's APIs (each on its own
   * port) instead of erroring out in a multi-stack app. Mutually
   * exclusive with a single-target selector (positional target /
   * `--stack` / explicit `--from-cfn-stack <name>`); the bare
   * `--from-cfn-stack` flag stays compatible (binds each routed stack
   * to its own CFn stack).
   */
  allStacks?: boolean;
  /** Pre-warm one container per Lambda at server boot. */
  warm: boolean;
  /** Pool size cap per Lambda (default 2, max 4). */
  perLambdaConcurrency: string;
  /** Skip docker pull for images. */
  pull: boolean;
  /**
   * Issue #249 / C8 — `--no-build` parity with `cdkl invoke`. Commander
   * maps `--no-build` to `build: boolean` (default `true`). When the
   * user passes `--no-build` the value flips to `false` and the
   * container-Lambda local-asset build is skipped (requires the
   * deterministic tag to already be in the local registry; errors with
   * an actionable message when missing). No-op for ZIP Lambdas and the
   * ECR-pull fallback.
   */
  build: boolean;
  /**
   * Issue #249 / C7 — `--ecr-role-arn` parity with `cdkl invoke`.
   * Optional role ARN forwarded to `pullEcrImage` on the
   * container-Lambda ECR-pull fallback for cross-account / centralized
   * registry pulls. Same-account / same-region pulls do not need this.
   */
  ecrRoleArn?: string;
  /** IP the host uses to bind/probe the RIE port (default 127.0.0.1). */
  containerHost: string;
  /** First Node.js inspector port; allocated contiguously per Lambda when set. */
  debugPortBase?: string;
  /**
   * Issue #249 / C14 — `--debug-port` alias for `--debug-port-base`
   * matching `cdkl invoke`'s flag name. When both are passed,
   * `--debug-port-base` (the canonical name) wins; otherwise this value
   * is used as the contiguous-range base.
   */
  debugPort?: string;
  envVars?: string;
  /**
   * Commander stores the raw `--assume-role` accumulator here. Each
   * `--assume-role <arn-or-pair>` invocation adds to it:
   *
   *   - `undefined` — flag not passed
   *   - `AssumeRoleOption` — one or more value-form tokens (global ARN
   *     and/or per-Lambda `<LogicalId>=<arn>` entries)
   *
   * Composed with {@link LocalStartApiOptions.assumeRoleAuto} at boot
   * via {@link normalizeStartApiAssumeRole} (also enforces the
   * `--assume-role-auto` vs global-ARN mutual exclusion guard) before
   * being threaded into {@link buildContainerSpec}.
   */
  assumeRole?: AssumeRoleOption;
  /**
   * Issue #256 Option 1 — `--assume-role-auto`: auto-resolve EACH
   * routed Lambda's own execution role per-Lambda. Surfaced as a
   * separate boolean flag (not the `[arn]` optional-value shape) so
   * Commander never silently overwrites the value-form accumulator
   * when the bare form is parsed after value forms. Composed with
   * {@link LocalStartApiOptions.assumeRole} at boot.
   */
  assumeRoleAuto?: boolean;
  /**
   * Issue #448: role to sts:AssumeRole before calling
   * `lambda:GetLayerVersion` for every literal-ARN entry in any
   * routed Lambda's `Properties.Layers`. Independent of `--assume-role`
   * (which scopes the handler's runtime AWS calls). Use only when the
   * dev credentials cannot read the layer (cross-account case);
   * AWS-published public layers like Lambda Powertools need no role.
   */
  layerRoleArn?: string;
  /** PR 8c: enable hot reload on `cdk.out/` + asset-dir changes. */
  watch: boolean;
  /** PR 8c: select a Stage by `StageName`; default is the first attached. */
  stage?: string;
  /**
   * Deprecated single-API alias for the positional `<targets...>` form.
   * Filters the discovered API surface to a single API by its logical id
   * (or, for Function URLs, the backing Lambda's logical id). Accepts
   * exactly ONE identifier; for a subset use the variadic positional
   * `cdkl start-api <id1> <id2>` form. Emits a deprecation warn on use.
   */
  api?: string;
  /**
   * Issue #606: alternative state source. Reads physical IDs from a
   * deployed CloudFormation stack via `ListStackResources`.
   * Commander maps:
   *   - flag absent → `undefined`
   *   - `--from-cfn-stack` (bare) → `true` (use the resolved stack name per routed stack)
   *   - `--from-cfn-stack <name>` → `'<name>'` (single named CFn stack)
   */
  fromCfnStack?: string | boolean;
  /**
   * Opt-in: DENY AWS_IAM SigV4 requests that cannot be cryptographically
   * verified (foreign access-key-id, or no local AWS credentials) instead
   * of the default warn-and-pass. Default `false` (warn-and-pass). Mirrors
   * the `--strict-sigv4` CLI flag.
   */
  strictSigv4?: boolean;
  /**
   * Region of the state record to read. Used with --from-cfn-stack
   * as the CFn client region.
   */
  stackRegion?: string;
  /**
   * Path to a PEM-encoded CA bundle. Client certificates that don't
   * chain to one of these CAs are rejected at the TLS handshake.
   * mTLS is enabled when ALL THREE `--mtls-truststore` / `--mtls-cert` /
   * `--mtls-key` flags are set; a partial set is a CLI-parse error.
   */
  mtlsTruststore?: string;
  /** Server-side mTLS certificate (PEM). Self-signed is fine for local dev. */
  mtlsCert?: string;
  /** Server-side mTLS private key (PEM). Must match `--mtls-cert`. */
  mtlsKey?: string;
  /** Host-injected extra state-source flag fields. */
  [key: string]: unknown;
}

/**
 * `cdkl start-api` — long-running local HTTP server that maps
 * synthesized API routes to Lambda invocations against the AWS Lambda
 * Runtime Interface Emulator (Docker required).
 *
 * Modeled on `sam local start-api` but reusing cdk-local's synthesis /
 * route-discovery / container plumbing. PR 8a scope:
 *   - REST v1 (AWS::ApiGateway::*) + HTTP API (AWS::ApiGatewayV2::*) +
 *     Function URL (AWS::Lambda::Url).
 *   - AWS_PROXY integrations only.
 *
 * PR 8b additions:
 *   - Authorizers: REST v1 Lambda TOKEN/REQUEST + Cognito User Pool;
 *     HTTP v2 Lambda REQUEST + JWT. Allow → claims/context flow into
 *     `event.requestContext.authorizer`. Deny → 401/403 written without
 *     invoking the route handler. Cognito / JWT verification falls back
 *     to pass-through mode when the JWKS endpoint is unreachable.
 *   - VPC-config Lambdas surface a startup warn line: the local
 *     container does NOT get attached to the deployed VPC.
 *
 * PR 8c additions (issue #235):
 *   - `--watch` enables hot reload: re-synths the CDK app and reloads
 *     routes when the source tree changes. Honors `cdk.json`'s
 *     `watch.include` / `watch.exclude` globs; `cdk.out/`,
 *     `node_modules`, and `.git` are ALWAYS excluded regardless of
 *     glob config.
 *   - HTTP API v2 OPTIONS preflight is intercepted when the API has a
 *     `CorsConfiguration`; REST v1 CORS (Mock OPTIONS method) stays
 *     out of scope.
 *   - `event.stageVariables` is populated from the selected Stage's
 *     `Variables` / `StageVariables` map. `--stage <name>` selects a
 *     specific Stage by name; default is the first Stage attached.
 *
 * Still deferred: WebSocket APIs.
 *
 * See [docs/cli-reference.md](../../../docs/cli-reference.md) for the
 * full surface and out-of-scope items.
 */
/**
 * Factory options for {@link createLocalStartApiCommand}.
 */
export interface CreateLocalStartApiCommandOptions {
  extraStateProviders?: ExtraStateProviders;
  /** Embed-time branding overrides for a host wrapping this factory. */
  embedConfig?: CdkLocalEmbedConfig;
}

/**
 * The `Server listening on <url>  (<label>)` ready banner, terminated with a
 * newline.
 *
 * D8.4 — load-bearing, and the most machine-parsed line in the repo: several
 * integ fixtures grep the prefix and extract the port from it, and
 * `cdkl studio` matches it with `SERVE_SPECS.api.readyRe` and then resolves
 * the captured URL with `new URL(...)` before it will front the serve with a
 * capture proxy. An authority the WHATWG parser rejects therefore does not
 * merely read badly — the studio serve is refused (issue #599).
 *
 * Exported so that contract has a unit test at the EMITTER. Both banner sites
 * live inside the long-running boot function, which has no cheap test seam,
 * so without this the only emitter-side coverage was the source fence.
 */
export function formatServerListeningBanner(
  scheme: string,
  host: string,
  port: number,
  pathSuffix: string,
  label: string
): string {
  return `Server listening on ${scheme}://${formatAuthority(host, port)}${pathSuffix}  (${label})\n`;
}

async function localStartApiCommand(
  targets: string[],
  options: LocalStartApiOptions,
  extraStateProviders: ExtraStateProviders | undefined
): Promise<void> {
  const logger = getLogger();
  if (options.verbose) {
    logger.setLevel('debug');
  }

  // Resolve the API filter set: the variadic positional `<targets...>`
  // names the SUBSET of APIs to serve (each on its own port), with the
  // union of every supplied identifier served. The deprecated `--api`
  // flag is a SINGLE-identifier alias kept for one release cycle — every
  // invocation that goes through it emits a deprecation warn so users see
  // the migration path before the next major bump removes it. `--api`
  // conflicts with any positional target (they would name overlapping or
  // contradictory subsets).
  let apiFilters: string[] = [...targets];
  if (options.api !== undefined) {
    if (targets.length > 0) {
      throw new Error(
        `Cannot specify both positional target(s) ([${targets.join(', ')}]) and --api flag ('${options.api}'). ` +
          `Use one or the other. The positional form is preferred — '--api' is a deprecated alias.`
      );
    }
    logger.warn(
      `[deprecated] --api <id> will be removed in a future major release. Use the positional argument instead: '${getEmbedConfig().cliName} start-api <id>'.`
    );
    apiFilters = [options.api];
  }

  // Bare `start-api` (no positional targets, no `--api`, no `--all-stacks`)
  // keeps its long-standing "serve every discovered API" default. In a TTY
  // we open a MULTI-SELECT picker (every API pre-selected) so the user can
  // press Enter to serve all or deselect to pick a subset; in a non-TTY
  // (CI, pipe) we serve all without prompting — start-api's one intentional
  // asymmetry vs invoke / run-task, which legitimately have no "serve all"
  // default. The actual prompt runs once inside `synthesizeAndBuild` (it
  // needs the synthesized API list), with `--watch` reloads reusing the
  // first selection via `interactivePicked`.
  const shouldPromptBare = shouldPromptBareMultiSelect(
    apiFilters,
    options.allStacks,
    isInteractive()
  );
  // Effective selectors driving synth stack-prefix inference: the
  // positional targets, or (after the bare multi-select fires) the picked
  // identifiers. Set inside `synthesizeAndBuild` when the picker runs.
  let effectiveTargets = [...apiFilters];
  const interactivePicked = { value: false };

  // Issue #55: `--all-stacks` serves every stack's API as a union, so it
  // cannot be combined with a selector that names a target subset.
  const allStacksConflictList = allStacksConflicts(options, targets, apiFilters);
  if (allStacksConflictList.length > 0) {
    throw new Error(
      `--all-stacks serves every stack's API and cannot be combined with a target subset selector (${allStacksConflictList.join(', ')}). ` +
        `Drop --all-stacks to target specific APIs, or drop the selector to serve them all. ` +
        `The bare --from-cfn-stack flag (no value) IS compatible with --all-stacks.`
    );
  }

  // Issue #256 Option 1: compose `--assume-role` (value-form
  // accumulator) and the separate `--assume-role-auto` boolean flag
  // into the in-process `AssumeRoleOption | undefined` representation
  // used downstream, AND enforce the bare-vs-global mutual-exclusion
  // guard at boot. Rejected combinations throw with an actionable
  // error before any synth / docker work.
  const normalizedAssumeRole = normalizeStartApiAssumeRole(
    options.assumeRole,
    options.assumeRoleAuto ?? false
  );
  if (normalizedAssumeRole === undefined) {
    delete options.assumeRole;
  } else {
    options.assumeRole = normalizedAssumeRole;
  }

  await applyRoleArnIfSet({
    roleArn: options.roleArn,
    region: options.region,
    profile: options.profile,
  });

  await ensureDockerAvailable();

  const appCmd = resolveApp(options.app);
  if (!appCmd) {
    throw new Error(
      `No CDK app specified. Pass --app, set ${getEmbedConfig().envPrefix}_APP, or add "app" to cdk.json.`
    );
  }

  const overrides = readEnvOverridesFile(options.envVars);
  // Issue #249 / C14 — `--debug-port` is a non-breaking alias of
  // `--debug-port-base`. Canonical (`--debug-port-base`) wins when both
  // are passed.
  const debugPortRaw = options.debugPortBase ?? options.debugPort;
  const debugPortBase = debugPortRaw ? parseDebugPort(debugPortRaw) : undefined;
  const perLambdaConcurrency = parsePerLambdaConcurrency(options.perLambdaConcurrency);
  // Track every tmpdir created by `materializeInlineCode` so the
  // graceful-shutdown path removes them. Long-running servers (this
  // command) would otherwise leak one tmpdir per inline-`Code.ZipFile`
  // Lambda per server invocation. Hot reload writes new tmpdirs into
  // the same set so the shutdown path is the single owner of cleanup.
  const inlineTmpDirs = new Set<string>();
  // PR 6 (#232): track every tmpdir created by layer merging too —
  // `materializeLambdaLayers(...)` produces one merged tmpdir per
  // Lambda whose `Properties.Layers` contains 2+ entries (single-
  // layer Lambdas bind-mount the layer's asset dir directly).
  // Cleaned up alongside `inlineTmpDirs` in `shutdown(...)`. Hot
  // reload (PR 8c) reuses this same set across reload firings; on
  // each `synthesizeAndBuild` re-run we record the new merged
  // tmpdirs (the previous iteration's entries stay behind until
  // shutdown — a follow-up PR can prune them per-reload, but the
  // shutdown path is the single owner of cleanup so leaks are
  // bounded by server lifetime).
  const layerTmpDirs = new Set<string>();
  // Synthesized AWS shared credentials file bind-mounted into every Lambda
  // container so handlers
  // using `fromIni({ profile })` explicitly resolve to the same creds as
  // the default chain. Created ONCE at server boot (NOT on hot reload —
  // re-resolving profile creds is fine for env vars but rewriting the
  // file mid-flight would race with running containers' SDK reads).
  // Disposed in the cleanup chain. Undefined when `--profile` is unset.
  let profileCredsFile: ProfileCredentialsFile | undefined;

  // PR 8b: per-server-lifecycle caches. Constructed once at server
  // startup; persisted across hot reloads (PR 8c) so authorizer
  // verdicts and JWKS keys aren't re-fetched on every reload. The
  // jwksWarnedAt Map (URL -> last-warn-at ms; #247) ensures the
  // "JWKS unreachable -> token accepted" warn re-emits every
  // WARN_REEMIT_INTERVAL_MS per URL — a long-running `--watch` session
  // keeps surfacing the degraded-auth state instead of silently accepting
  // every token after the first warn.
  const authorizerCache = createAuthorizerCache();
  const jwksCache = createJwksCache();
  const jwksWarnedAt: WarnedAt = new Map<string, number>();
  // #447: SigV4 verifier state for `AuthorizationType: 'AWS_IAM'` routes.
  // The credentials loader is constructed eagerly but the credential
  // chain itself is only hit on the first IAM-protected request — see
  // `defaultCredentialsLoader` for the caching contract.
  let sigV4CredentialsLoader: CredentialsLoader | undefined;
  const sigV4WarnedForeignIds = new Set<string>();
  // One-shot guard for the `--from-cfn-stack <name>` redundancy tip
  // (improvement A). `synthesizeAndBuild` runs on every `--watch` hot
  // reload firing, so without this flag the tip would re-emit on every
  // reload — noisy. The flag flips on the first emission and stays
  // `true` for the rest of the server lifetime; subsequent reloads see
  // the gate and skip the emission. Per-`localStartApiCommand`-invocation
  // (new server boot starts with a fresh `false`).
  const fromCfnTipEmitted: { value: boolean } = { value: false };
  // One-shot guard for the per-identifier "did not match; ignored" warning
  // from the target-subset resolver. `synthesizeAndBuild` re-runs on every
  // `--watch` hot reload firing, so without this set a typo'd identifier
  // would re-warn on every file change. Each unmatched id is warned at most
  // ONCE per `start-api` run; subsequent reloads see the id already in the
  // set and skip it. Per-`localStartApiCommand`-invocation (new server boot
  // starts with a fresh empty set).
  const unmatchedTargetWarned = new Set<string>();

  /**
   * One synth + discover + build pass. Returns the next-state
   * material. Reused on initial boot AND every hot-reload firing.
   * Failures bubble up — the orchestrator catches them and keeps the
   * old state; the initial boot lets them propagate so the CLI exits
   * with a clear error before "Server listening" is ever printed.
   *
   * PR 8b: also runs `attachAuthorizers` after route discovery so the
   * resulting `RouteWithAuth[]` carries every route's authorizer info.
   * Hot reload picks up authorizer-config changes via this re-run.
   */
  const synthesizeAndBuild = async (): Promise<NextStateMaterial> => {
    logger.info('Synthesizing CDK app...');
    const synthesizer = new Synthesizer();
    const context = parseContextOptions(options.context);
    const synthOpts: SynthesisOptions = {
      app: appCmd,
      output: options.output,
      ...(options.region && { region: options.region }),
      ...(options.profile && { profile: options.profile }),
      ...(Object.keys(context).length > 0 && { context }),
    };
    const { stacks } = await synthesizer.synthesize(synthOpts);

    // Bare multi-select — runs ONCE (the first synth), guarded so
    // `--watch` reloads reuse the first selection rather than re-prompting.
    // Every API is pre-selected (Enter = serve all); the user deselects to
    // pick a subset. Sets both `apiFilters` (route filter set) and
    // `effectiveTargets` (stack inference) so the picked subset drives the
    // rest of the pass.
    if (shouldPromptBare && !interactivePicked.value) {
      const apis = listTargets(stacks).apis;
      if (apis.length === 0) {
        throw new Error(
          `No APIs found in this CDK app to choose from. Run \`${getEmbedConfig().cliName} list\` to see what is available.`
        );
      }
      const picked = await pickManyTargets('Select APIs to serve', apis);
      apiFilters = picked;
      effectiveTargets = picked;
      interactivePicked.value = true;
    }

    // When the user passes an explicit `--from-cfn-stack <name>` AND has
    // not also passed `--stack`, infer the synth target from the CFn
    // stack name — typical CDK apps deploy each stack under its own
    // stack-name, so the same value selects the synth target unambiguously.
    // `--from-cfn-stack` without a value (bare flag => `true`) is left to
    // the regular single-stack auto-pick path.
    const cfnStackFallback =
      typeof options.fromCfnStack === 'string' ? options.fromCfnStack : undefined;
    // Improvement B (extended to subsets): synth stack-prefix inference.
    // `cdkl start-api MyStack/MyApi` (no `--stack`, no `--from-cfn-stack`)
    // extracts `MyStack` from the target's `<StackName>/<construct>` shape
    // and uses it as a third fallback for stack selection. With MULTIPLE
    // targets we only keep the optimization when they all share ONE stack
    // prefix; targets spanning different stacks (or any bare-logical-id
    // target without a `/` separator) leave this undefined so the synth
    // scope falls back to ALL stacks (item 5) and the union is filtered
    // down afterwards by `apiFilters`.
    const targetStackPrefix = deriveSynthStackPrefix(effectiveTargets);
    const targetStacks = pickTargetStacks(
      stacks,
      options.stack,
      cfnStackFallback,
      targetStackPrefix,
      // Fall back to synthesizing ALL stacks when an explicit subset spans
      // (or might span) multiple stacks and no other selector pins one.
      options.allStacks || shouldSynthAllStacks(effectiveTargets, options.stack, cfnStackFallback)
    );
    if (targetStacks.length === 0) {
      throw new Error(
        'No stacks matched. Pass --stack <name> (or --from-cfn-stack <name>) or run from a single-stack app.'
      );
    }

    // Improvement A: when the user passed `--from-cfn-stack <explicit>`
    // AND that value already equals the routed stack's `stackName`, the
    // explicit value is redundant — the bare-flag form auto-resolves to
    // the same value. Surface a single info-level tip so the user knows
    // they can drop the value next time. Skipped on multi-stack runs
    // (too many edge cases to keep the message useful).
    //
    // `synthesizeAndBuild` re-runs on every `--watch` hot reload firing,
    // so the emission is gated by `fromCfnTipEmitted.value` (one-shot per
    // server lifetime) — see `tryEmitFromCfnRedundancyTipOnce`.
    const routedStackNames = targetStacks.map((s) => s.stackName);
    tryEmitFromCfnRedundancyTipOnce(
      options.fromCfnStack,
      routedStackNames,
      fromCfnTipEmitted,
      (routedStackName) => {
        logger.info(
          `tip: --from-cfn-stack value matches the routed stack name (${routedStackName}); you can omit the value: \`${getEmbedConfig().cliName} start-api ... --from-cfn-stack\` (bare flag) resolves to the same value.`
        );
      }
    );

    const routes = discoverRoutes(targetStacks);
    // #462: WebSocket APIs (ProtocolType: 'WEBSOCKET') are discovered
    // through a sibling pipeline so the resulting routes (keyed by
    // RouteKey rather than by method+path) stay structurally separate
    // from the HTTP / REST / Function URL `DiscoveredRoute[]`. Errors
    // are aggregated into the same boot-time warn pass so a malformed
    // WebSocket API does not abort sibling HTTP API boot.
    const wsDiscovery = discoverWebSocketApis(targetStacks);
    if (wsDiscovery.errors.length > 0) {
      for (const e of wsDiscovery.errors) {
        logger.warn(`WebSocket discovery: ${e}`);
      }
    }
    // `let` so the target-subset filter (issue #311) can narrow it to the
    // WS APIs the explicit `<target...>` named.
    let webSocketApis = wsDiscovery.apis;
    if (routes.length === 0 && webSocketApis.length === 0) {
      throw new Error(
        `No supported API routes were discovered. ${getEmbedConfig().cliName} start-api supports AWS::ApiGateway::* (REST v1), AWS::ApiGatewayV2::* (HTTP + WebSocket), and AWS::Lambda::Url (Function URL) with AWS_PROXY integrations only.`
      );
    }

    // PR 8c: stage selection + variable injection. Build the per-API
    // Stage map for every target stack and attach it to the routes.
    // Stage selection is `--stage <name>` global override, otherwise
    // first-attached default. The CLI surfaces a warn line when
    // `--stage` was passed and at least one API doesn't have a Stage
    // with that name.
    const stageMap = new Map<string, ResolvedStage>();
    for (const stack of targetStacks) {
      const m = buildStageMap(stack.template, options.stage);
      for (const [k, v] of m) stageMap.set(k, v);
    }
    if (options.stage) {
      // Walk the routes looking for HTTP API v2 / REST v1 routes whose
      // API isn't in `stageMap` (i.e. the API had no Stage with the
      // override name). One warn per such API, deduplicated.
      const missingApis = new Set<string>();
      for (const r of routes) {
        if (!r.apiLogicalId) continue;
        if (!stageMap.has(r.apiLogicalId)) missingApis.add(r.apiLogicalId);
      }
      for (const apiId of missingApis) {
        logger.warn(
          `--stage '${options.stage}' did not match any Stage on API '${apiId}'; routes on that API will get stageVariables: null.`
        );
      }
    }
    attachStageContext(routes, stageMap);

    // PR 8b: attach authorizer info to every route. Routes without an
    // authorizer pass through as `{route, authorizer: undefined}`.
    // Routes referencing an unsupported authorizer kind hard-fail here.
    let routesWithAuth = attachAuthorizers(targetStacks, routes);

    // Target subset filter — restrict the discovered surface to the UNION
    // of the supplied identifiers. Useful when the user wants exactly a
    // few servers (e.g. to free other ports, or to focus testing on a
    // couple of APIs / Function URLs).
    //
    // The pure resolver enforces strict multi-stack bare-id rejection +
    // empty-union rejection (both THROW) and returns the surviving union
    // plus the identifiers that matched nothing. Available identifiers are
    // computed once inside it (not per-id) for the O(N·M) bound.
    if (apiFilters.length > 0) {
      const { filtered, filteredWebSocketApis, unmatched } = resolveApiTargetSubset(
        routesWithAuth,
        apiFilters,
        targetStacks.map((s) => s.stackName),
        webSocketApis
      );
      // Surface any individual identifier that matched nothing (the union
      // still has routes / WS APIs from its siblings, so the run continues) —
      // a single typo in a multi-target list otherwise silently serves a
      // smaller set than the user asked for. Gated one-shot per identifier
      // (`unmatchedTargetWarned`) so a `--watch` hot reload doesn't re-warn
      // the same typo on every file change.
      if (unmatched.length > 0) {
        const available =
          [
            ...availableApiIdentifiers(routesWithAuth),
            ...availableWebSocketApiIdentifiers(webSocketApis),
          ].join(', ') || '(none)';
        for (const id of unmatched) {
          if (unmatchedTargetWarned.has(id)) continue;
          unmatchedTargetWarned.add(id);
          logger.warn(
            `Target '${id}' did not match any discovered API; it is ignored. Available identifiers: ${available}.`
          );
        }
      }
      routesWithAuth = filtered;
      // Issue #311: narrow the served WS APIs to the target subset too (they
      // were previously served in full regardless of the target).
      webSocketApis = filteredWebSocketApis;
    }

    // PR 8c: per-API CORS config. HTTP API v2's `CorsConfiguration` +
    // Function URL's own `Cors` (issue #644) + CORS borrowed from
    // CloudFront ResponseHeadersPolicy when a CloudFront Distribution
    // fronts the Function URL (issue #646 — production-correct CDK
    // pattern where CORS is on the edge, not the origin). REST v1
    // OPTIONS Mock integrations stay out of scope.
    //
    // Merge order: direct map last → Function URL's own `Cors` block
    // wins over a CloudFront-borrowed config when both exist (matches
    // AWS semantics: Function URL's own headers apply at the Lambda
    // boundary; we only have one slot locally, so closer-to-Lambda
    // wins). The two rarely coexist in practice.
    const corsConfigByApiId = new Map<string, CorsConfig>();
    for (const stack of targetStacks) {
      const fromCloudFront = buildCorsConfigFromCloudFrontChain(stack.template);
      for (const [k, v] of fromCloudFront) corsConfigByApiId.set(k, v);
      const direct = buildCorsConfigByApiId(stack.template);
      for (const [k, v] of direct) corsConfigByApiId.set(k, v);
    }

    // `--from-state` / `--from-cfn-stack` (issue #606): load deployed
    // state for every routed stack once per synth (= initial boot AND
    // every hot-reload firing). We do this outside the per-Lambda loop
    // so a stack with N reachable Lambdas only pays one state-load
    // round-trip. Pseudo parameters are also resolved once per stack —
    // STS GetCallerIdentity is account-wide so the bag is identical
    // across same-region stacks, but the partition / URL suffix can
    // differ if stacks span partitions. Per-stack failures degrade to
    // warn-and-fall-back (the PR 1 behavior is preserved) so a missing
    // or unreadable state file never aborts the server boot.
    const stateSourceActive =
      isCfnFlagPresent(options) || hasExtraStateProviderActive(options, extraStateProviders);
    const stateByStack = stateSourceActive
      ? await loadStateForRoutedStacks(
          targetStacks,
          routes,
          routesWithAuth,
          options,
          extraStateProviders
        )
      : new Map<string, StackStateBundle>();

    // Issue #654: resolve `--profile <p>` to a concrete credential set
    // ONCE at boot for forwarding into every Lambda container that
    // doesn't have its own `--assume-role`. Drives the SDK's default
    // credential provider chain (SSO / IAM Identity Center / fromIni /
    // role-assumption profiles all handled uniformly — same chain that
    // already resolves `--profile` for the host's own AWS SDK clients).
    // Resolved here so a stack with N Lambdas pays one STS round-trip.
    // SSO temp creds typically last 1h+; long-running `--watch` sessions
    // outliving the creds need a cdk-local restart (auto-refresh deferred,
    // see issue #654).
    const profileCredentials = options.profile
      ? await resolveProfileCredentials(options.profile)
      : undefined;

    // When `--profile <p>` is set, ALSO synthesize a shared AWS
    // credentials file with the
    // resolved creds under `[<p>]` and bind-mount it into every Lambda
    // container at `/cdk-local-aws/credentials`. This lets handlers that
    // use `fromIni({ profile: '<p>' })` explicitly inside their code
    // (instead of the default credential chain) resolve to the same
    // creds locally. The default-chain path (most handlers) keeps
    // working through the existing `AWS_*` env-var injection — the
    // file is the additive layer for the explicit-profile case.
    //
    // Caveat: under `--watch` hot reload, `profileCredentials` re-resolves
    // and the new env vars flow to the next cold start, but the on-disk
    // file is NOT re-written. Long-running sessions whose SSO temp creds
    // expire need a cdk-local restart (matches the auto-refresh-deferred
    // stance for env vars).
    //
    // Assigned to outer-scope `profileCredsFile` so the cleanup chain
    // disposes the file at shutdown. Only ever assigned on the FIRST
    // synthesizeAndBuild (initial server boot) — hot reload sees the
    // variable already set and skips re-writing per the caveat above.
    if (options.profile && profileCredentials && !profileCredsFile) {
      profileCredsFile = await writeProfileCredentialsFile(options.profile, profileCredentials);
    }

    // Build the per-Lambda spec map. Every reachable logical ID is
    // resolved to its asset / inline code, env vars, optional STS creds
    // (--assume-role), optional --debug-port reservation. The container
    // pool then knows everything it needs to lazy-start a fresh one.
    // Authorizer Lambdas are also pooled — they're invoked just like
    // route handlers (PR 8b).
    const lambdaIds = uniqueLambdaIds(routes, routesWithAuth, webSocketApis);
    const specs = new Map<string, ContainerSpec>();
    for (let i = 0; i < lambdaIds.length; i++) {
      const logicalId = lambdaIds[i]!;
      const spec = await buildContainerSpec({
        logicalId,
        stacks: targetStacks,
        overrides,
        assumeRole: normalizedAssumeRole,
        containerHost: options.containerHost,
        ...(debugPortBase !== undefined && { debugPort: debugPortBase + i }),
        stsRegion: options.region ?? process.env['AWS_REGION'] ?? process.env['AWS_DEFAULT_REGION'],
        inlineTmpDirs,
        layerTmpDirs,
        stateByStack,
        skipPull: options.pull === false,
        skipBuild: options.build === false,
        ...(options.profile !== undefined && { profile: options.profile }),
        ...(options.ecrRoleArn !== undefined && { ecrRoleArn: options.ecrRoleArn }),
        ...(options.layerRoleArn !== undefined && { layerRoleArn: options.layerRoleArn }),
        ...(profileCredentials && { profileCredentials }),
        ...(profileCredsFile && { profileCredsFile }),
        ...(profileCredentials?.region && { profileRegion: profileCredentials.region }),
        ...(options.stackRegion && { stackRegionOverride: options.stackRegion }),
      });
      specs.set(logicalId, spec);
    }

    // Pull every distinct base image up front so the first request
    // doesn't pay the layer-pull cost. Mirrors `cdkl invoke`'s
    // pull pass. Only the ZIP branch needs a base-image pull here —
    // IMAGE-variant specs already have their per-Lambda image
    // resolved by `buildContainerSpec` (`resolveContainerImageForStartApi`
    // ran the local build or ECR pull before this point), so the
    // ContainerPool can `docker run` against it without any further
    // pull step.
    const distinctImages = new Set<string>();
    for (const spec of specs.values()) {
      if (spec.kind === 'zip') {
        distinctImages.add(resolveRuntimeImage(spec.lambda.runtime));
      }
    }
    for (const image of distinctImages) {
      await pullImage(image, options.pull === false);
    }

    return {
      routes: routesWithAuth,
      specs,
      corsConfigByApiId,
      webSocketApis,
      stacks: targetStacks,
    };
  };

  /**
   * Helper: build a {@link ContainerPool} from a spec map and tag it
   * with the spec map (via the non-enumerable `__cdklSpecs` property)
   * so the reload orchestrator can compute spec diffs.
   */
  const buildPool = (specs: Map<string, ContainerSpec>): ContainerPool => {
    const pool = createContainerPool(specs, {
      perLambdaConcurrency,
      skipPull: options.pull === false,
    });
    Object.defineProperty(pool, '__cdklSpecs', {
      value: specs,
      enumerable: false,
      configurable: true,
    });
    return pool;
  };

  // Initial boot.
  const initialMaterial = await synthesizeAndBuild();

  // PR 8b: pre-warm JWKS for Cognito / JWT authorizers so the first
  // request doesn't pay the fetch latency. Failures fall through to
  // pass-through mode with the warn line documented in cognito-jwt.ts.
  await prewarmJwks(initialMaterial.routes, jwksCache);

  // PR 8b: VPC-config Lambdas warn at startup. cdk-local does NOT block
  // these routes, but the developer should know the local container
  // reaches external services via the host's network rather than
  // through the deployed VPC's NAT / private subnets. Re-runs on hot
  // reload would be noisy; we emit this once at initial boot only.
  warnVpcConfigLambdas(initialMaterial.routes, initialMaterial.stacks ?? []);

  // #447: AWS_IAM-protected routes warn at startup. The local server
  // verifies SigV4 signatures only — IAM policy evaluation (resource /
  // action / condition) is NOT emulated.
  //
  // The credentials loader is ALWAYS constructed at boot (not gated on
  // the initial template having IAM routes) so that hot-reload
  // (`--watch`) paths that ADD a new IAM route after boot have the
  // loader already wired up. The loader is internally lazy
  // (`defaultCredentialsLoader()` memoizes the actual STSClient +
  // credential resolution until first call), so unused boots pay zero
  // cost. Pre-fix the loader was conditionally constructed at this
  // point only when the initial template had IAM routes, which caused
  // post-hot-reload IAM routes to hit the http-server's defensive
  // "no SigV4 credentials loader configured — denying" deny path with
  // no explanation. PR #484 review MAJOR.
  sigV4CredentialsLoader = defaultCredentialsLoader();
  warnIamRoutes(initialMaterial.routes);

  // RIE invoke timeout: 2x the slowest Lambda's Timeout, floor 30s.
  let maxTimeoutSec = 0;
  for (const spec of initialMaterial.specs.values()) {
    if (spec.lambda.timeoutSec > maxTimeoutSec) maxTimeoutSec = spec.lambda.timeoutSec;
  }
  const rieTimeoutMs = Math.max(30_000, maxTimeoutSec * 2 * 1000);

  const basePort = parseInt(options.port, 10);
  if (!Number.isFinite(basePort) || basePort < 0 || basePort > 65535) {
    throw new Error(`--port must be 0..65535 (got ${options.port}).`);
  }

  // mTLS resolution: all-or-none. When any of the three flags is set,
  // ALL THREE must be set — partial flag sets are rejected at CLI-parse
  // time so the server never boots in a half-configured state. The TLS
  // handshake itself (in `https.createServer({requestCert: true,
  // rejectUnauthorized: true, ca, cert, key})`) enforces the
  // client-cert chain check against the CA bundle — there is no
  // per-request validation in cdk-local's code path.
  const mtlsConfig: MtlsServerConfig | undefined = resolveMtlsConfig(options);
  if (mtlsConfig) {
    logger.info(
      'mTLS enabled: client certificates required (chain check against --mtls-truststore at TLS handshake).'
    );
  }

  // Issue #260: one HTTP server per API. Group the routes by API surface
  // (HTTP API logical id / REST API logical id / Function URL backing
  // Lambda) and launch one `startApiServer` per group. Each server gets
  // its own ContainerPool (filtered to the Lambdas reachable from that
  // group's routes) so authorizers, CORS configs, and stage variables
  // are scoped to the correct API and never bleed across them.
  const initialGroups = groupRoutesByServer(initialMaterial.routes);
  // basePort is the FIRST server's port; subsequent servers get
  // basePort+1, basePort+2, ... When basePort is 0 every server
  // auto-allocates. Auto-allocation is fine even across multiple
  // servers because the OS picks distinct ports.
  const servers: BootedApiServer[] = [];
  let nextPort = basePort;
  for (const group of initialGroups) {
    const groupSpecs = filterSpecsForGroup(group, initialMaterial.specs);
    const groupPool = buildPool(groupSpecs);
    const groupState: ServerState = {
      routes: group.routes,
      pool: groupPool,
      corsConfigByApiId: initialMaterial.corsConfigByApiId,
    };
    // Optional pre-warm: one container per Lambda, in parallel.
    if (options.warm) {
      logger.info(`Pre-warming ${groupSpecs.size} container(s) for ${group.displayName}...`);
      const handles = await Promise.allSettled(
        [...groupSpecs.keys()].map((id) => groupPool.acquire(id))
      );
      for (const result of handles) {
        if (result.status === 'fulfilled') {
          groupPool.release(result.value);
        } else {
          logger.warn(
            `Pre-warm failed for one Lambda in ${group.displayName} (cold start cost will apply on first request): ${result.reason instanceof Error ? result.reason.message : String(result.reason)}`
          );
        }
      }
    }
    const defaultRegion =
      options.region ?? process.env['AWS_REGION'] ?? process.env['AWS_DEFAULT_REGION'] ?? undefined;
    const started = await startApiServer({
      state: groupState,
      rieTimeoutMs,
      host: options.host,
      ...(mtlsConfig && { mtls: mtlsConfig }),
      // Increment per server; basePort=0 leaves every server on auto-alloc.
      port: basePort === 0 ? 0 : nextPort,
      authorizerCache,
      jwksCache,
      jwksWarnedAt,
      sigV4CredentialsLoader,
      sigV4WarnedForeignIds,
      sigV4Strict: options.strictSigv4 === true,
      // #458: surfaces as the per-route fallback region for HTTP API
      // v2 service integrations. Per-request `RequestParameters.Region`
      // overrides this — matches AWS API Gateway behavior.
      ...(defaultRegion && { defaultRegion }),
    });
    servers.push({ group, server: started });
    if (basePort !== 0) nextPort += 1;
  }

  // #462: WebSocket API server boot loop. One HTTP server per
  // discovered WebSocket API; each server hosts an upgrade-event
  // listener via `attachWebSocketServer` AND a pre-pass HTTP handler
  // for the `@connections/<id>` data plane. Lambda containers in the
  // pool are injected with `AWS_ENDPOINT_URL_APIGATEWAYMANAGEMENTAPI`
  // pointing at the same port so `apigatewaymanagementapi:PostToConnection`
  // from inside the handler lands on cdk-local's local endpoint.
  const wsServers: BootedWebSocketServer[] = [];
  const initialWsApis = initialMaterial.webSocketApis ?? [];
  warnUnsupportedWebSocketApis(initialWsApis, logger);

  // Issue #527 M2: probe Docker server version ONCE per session before
  // any WebSocket API attaches. The `--add-host=host.docker.internal:host-gateway`
  // mapping cdk-local injects requires Docker 20.10+; older daemons silently
  // fail with `ENOTFOUND host.docker.internal` at SDK-call time. Only
  // probe when at least one ATTACHABLE WebSocket API exists — HTTP /
  // REST-only sessions don't use the host-gateway mapping and shouldn't
  // pay the docker subprocess hop. `unsupported`-tagged APIs are also
  // skipped (they never enter the attach loop).
  const attachableWsApis = initialWsApis.filter((api) => !api.unsupported);
  if (attachableWsApis.length > 0) {
    const probe = await probeHostGatewaySupport();
    if (!probe.supported) {
      throw new Error(
        `${getEmbedConfig().cliName} start-api requires Docker ${HOST_GATEWAY_MIN_VERSION.major}.${HOST_GATEWAY_MIN_VERSION.minor}+ ` +
          `for WebSocket API support (--add-host=host.docker.internal:host-gateway needs the 20.10 host-gateway alias). ` +
          `Detected server version: ${probe.rawVersion || '<empty — daemon unreachable or output stripped>'}. ` +
          `Upgrade Docker, or remove the WebSocket API from this app to fall back to HTTP-only start-api.`
      );
    }
    if (probe.parsed === null) {
      logger.warn(
        `Docker server version "${probe.rawVersion}" did not match the canonical "<major>.<minor>" shape; ` +
          `assuming host-gateway support. If WebSocket containers fail to reach the local server, ` +
          `verify your Docker-compatible CLI honors --add-host=host.docker.internal:host-gateway.`
      );
    }
  }

  for (const api of initialWsApis) {
    // Skip APIs flagged unsupported at discovery — typical cause is a
    // non-NONE `AuthorizationType` on `$connect` (cdkl v1 does not
    // emulate WebSocket authorizers; admitting unauthenticated clients
    // would diverge from AWS-deployed behavior). The warn fired above
    // names the affected routes; here we just skip the attach loop so
    // no upgrade is accepted on this API.
    if (api.unsupported) continue;
    const wsLambdaIds = new Set(api.routes.map((r) => r.targetLambdaLogicalId));
    const wsSpecs = new Map<string, ContainerSpec>();
    for (const id of wsLambdaIds) {
      const spec = initialMaterial.specs.get(id);
      if (spec) wsSpecs.set(id, spec);
    }
    if (wsSpecs.size === 0) {
      logger.warn(
        `WebSocket API ${api.declaredAt}: no resolvable Lambda backing routes; skipping.`
      );
      continue;
    }
    const wsPool = buildPool(wsSpecs);
    const wsState: ServerState = {
      routes: [],
      pool: wsPool,
      corsConfigByApiId: new Map(),
    };
    // The HTTP server hosting the WebSocket also serves the
    // management-API `@connections/<id>` calls. Build the
    // {@link AttachedWebSocketServer} AFTER the http server is bound
    // so we know the port — needed to inject
    // `AWS_ENDPOINT_URL_APIGATEWAYMANAGEMENTAPI` into every WebSocket
    // Lambda's env map BEFORE the pool starts the first container.
    const wsApiPath = `/${api.stage}`;
    // Forward-decl the registry pointer so the preDispatch closure
    // can read it once we attach below.
    let registryRef: AttachedWebSocketServer | undefined;
    const started = await startApiServer({
      state: wsState,
      rieTimeoutMs,
      host: options.host,
      port: basePort === 0 ? 0 : nextPort,
      authorizerCache,
      jwksCache,
      jwksWarnedAt,
      sigV4WarnedForeignIds,
      sigV4Strict: options.strictSigv4 === true,
      preDispatch: async (req, res) => {
        if (!registryRef) return false;
        return handleManagementRequest(req, res, registryRef.registry);
      },
    });
    const attached = attachWebSocketServer({
      httpServer: started.server,
      pool: wsPool,
      rieTimeoutMs,
      apis: [{ api, apiPath: wsApiPath }],
    });
    registryRef = attached;

    // Inject the management-API endpoint URL into every WebSocket
    // Lambda's container env BEFORE any cold-start. The container
    // pool reads `spec.env` at `acquire()` time, so mutating the
    // shared map after the server is bound but before the first
    // request hits the route is correct. The mutation is per-Lambda:
    // only Lambdas backing this WebSocket API see the override; the
    // sibling HTTP / REST Lambdas' env maps stay untouched.
    //
    // Placeholder credentials: the AWS SDK v3 client constructor
    // requires SOME credentials to sign requests, even when the
    // endpoint override points at a local server that ignores
    // SigV4. When neither `--assume-role` nor inherited
    // `AWS_ACCESS_KEY_ID` etc. are set, the SDK errors with
    // `CredentialsProviderError: Could not load credentials from
    // any providers`. We populate fake credentials per the
    // ecs-network.ts metadata-sidecar precedent — cdk-local's
    // `@connections` handler doesn't verify SigV4, so the values
    // are opaque.
    //
    // Endpoint hostname: the URL is consumed INSIDE the Lambda
    // container, so `127.0.0.1` would be the container's own
    // loopback — not the host. On Docker Desktop (macOS / Windows)
    // `host.docker.internal` resolves to the host. Linux native
    // dockerd doesn't expose this hostname by default but Docker
    // 20.10+ supports `--add-host=host.docker.internal:host-gateway`
    // at runtime; we forward that flag via `extraHostMappings` on
    // the container spec for every WebSocket Lambda so the URL
    // always resolves on every platform. The host port is the
    // local server's bound port.
    //
    // Stage path: the URL INCLUDES `/${api.stage}` to match the
    // AWS-docs-canonical handler shape. The deployed
    // apigatewaymanagementapi endpoint URL is
    // `https://<api-id>.execute-api.<region>.amazonaws.com/<stage>`,
    // so SDK clients built from `domainName + stage` produce
    // `POST /<stage>/@connections/<id>`. Mirror that exactly for the
    // env-var override path so handlers that build the SDK client
    // with `new ApiGatewayManagementApiClient({})` (and let the
    // SDK pick up the env override) AND handlers that build it
    // with the explicit `{endpoint: 'https://' + domainName + '/' + stage}`
    // shape both work without per-handler code differences.
    // The pre-fix endpoint dropped `/${stage}` and any SDK call
    // that included the stage segment hit a 404 against the local
    // parser.
    const mgmtEndpoint = buildMgmtEndpointEnvUrl('host.docker.internal', started.port, api.stage);
    const hostGatewayMapping: { host: string; ip: string }[] = [
      { host: 'host.docker.internal', ip: 'host-gateway' },
    ];
    for (const id of wsLambdaIds) {
      const spec = initialMaterial.specs.get(id);
      if (!spec) continue;
      spec.env['AWS_ENDPOINT_URL_APIGATEWAYMANAGEMENTAPI'] = mgmtEndpoint;
      if (!spec.env['AWS_ACCESS_KEY_ID']) {
        spec.env['AWS_ACCESS_KEY_ID'] = 'AKIAIOSFODNN7EXAMPLE';
        spec.env['AWS_SECRET_ACCESS_KEY'] = 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY';
      }
      // Ensure the SDK has a region — apigatewaymanagementapi
      // clients refuse to instantiate without one.
      if (!spec.env['AWS_REGION']) {
        spec.env['AWS_REGION'] =
          options.region ??
          process.env['AWS_REGION'] ??
          process.env['AWS_DEFAULT_REGION'] ??
          'us-east-1';
      }
      // Mutate the spec to include host.docker.internal mapping so
      // the Lambda's `apigatewaymanagementapi:PostToConnection` URL
      // resolves to the host's cdk-local server on every OS (Linux native
      // dockerd does NOT auto-expose `host.docker.internal`; macOS /
      // Windows Docker Desktop does).
      const merged = [...(spec.extraHosts ?? []), ...hostGatewayMapping];
      spec.extraHosts = merged;
    }

    wsServers.push({ api, server: started, attached, apiPath: wsApiPath });
    if (basePort !== 0) nextPort += 1;
  }

  printPerServerRouteTables(servers);
  const allRoutes = servers.flatMap((s) => s.group.routes.map((r) => r.route));
  warnUnsupportedRoutes(allRoutes, logger);
  warnSsrfRiskyIntegrations(allRoutes, logger);
  logger.info(
    `Per-Lambda concurrency: ${perLambdaConcurrency} (override with --per-lambda-concurrency)`
  );
  // D8.4 — load-bearing: verify.sh greps for this exact prefix.
  // Emit one line per server so verify.sh / users can match each API to
  // its port. When mTLS is active, the scheme flips to `https://` and
  // verify.sh / users have to pass `--cacert` + `--cert` + `--key` to
  // curl; both schemes share the "Server listening on " prefix so the
  // verify.sh marker scan is unchanged.
  for (const { group, server } of servers) {
    process.stdout.write(
      formatServerListeningBanner(server.scheme, server.host, server.port, '', group.displayName)
    );
  }
  // #462: emit one banner per WebSocket server. The protocol prefix
  // is `ws://` / `wss://` rather than `http://` / `https://` so users
  // copy-paste the URL straight into `wscat -c <URL>` / browser
  // `new WebSocket(url)`.
  for (const ws of wsServers) {
    const scheme = ws.server.scheme === 'https' ? 'wss' : 'ws';
    process.stdout.write(
      formatServerListeningBanner(
        scheme,
        ws.server.host,
        ws.server.port,
        ws.apiPath,
        `${ws.api.apiLogicalId} (WebSocket API)`
      )
    );
  }
  process.stdout.write('^C to stop and clean up containers.\n');

  // PR 8c (extended for issue #260 to span N servers): hot reload
  // (`--watch`). For N-server topology we serialize re-synth ONCE per
  // watcher event, then per-server filter the material + swap state.
  // Adding/removing an entire API across a reload is not supported —
  // the user is warned and the server set stays static until restart.
  let watcher: FileWatcher | undefined;
  let reloadChain: Promise<unknown> = Promise.resolve();
  if (options.watch) {
    // Watch the CDK app's source tree (the synth working directory, where
    // `cdk.json` lives) so editing handler / construct source re-synths
    // and hot-reloads. We honor `cdk.json`'s `watch.include` /
    // `watch.exclude` (mirroring `cdk watch`).
    const watchRoot = process.cwd();
    const { ignored, shouldTrigger, excludePatterns } = createWatchPredicates({
      watchRoot,
      output: options.output,
      watchConfig: resolveWatchConfig(),
    });
    watcher = createFileWatcher({
      paths: [watchRoot],
      ignored,
      shouldTrigger,
      onChange: () => {
        logger.info('Detected source change; reloading...');
        const next = reloadChain.then(() =>
          reloadAllServers({
            synthesizeAndBuild,
            servers,
            buildPool,
            logger,
          })
        );
        reloadChain = next.catch(() => undefined);
      },
    });
    logger.info(
      `Watching ${watchRoot} for source changes (excluding ${excludePatterns.join(', ')}).`
    );
  }

  // Graceful shutdown: SIGINT / SIGTERM / uncaughtException /
  // unhandledRejection all run the same dispose path. Double-^C
  // bypasses dispose and exits immediately so the user can escape a
  // hung Docker daemon.
  //
  // Single-flight contract (closes the SIGINT-during-SIGTERM /
  // double-signal race): the actual cleanup body is wrapped in
  // `singleFlight(...)` so a second signal that lands while the first
  // shutdown is still draining `pool.dispose()` awaits the same
  // promise instead of starting a parallel run against the shared
  // `servers` / `inlineTmpDirs` / `layerTmpDirs` cells (which would
  // otherwise double-`server.close()` and corrupt the
  // mid-iteration tmpdir set). The first signal's `signal` + `exitCode`
  // win — subsequent signals' arguments are intentionally dropped.
  // The double-^C force-exit feature is preserved by tracking the
  // started + completed state separately from the in-flight cleanup.
  let shutdownStarted = false;
  let firstSignal: string | undefined;
  let firstExitCode = 0;
  let forceExitArmed = false;
  const runCleanup = singleFlight(async (): Promise<void> => {
    logger.info(`Received ${firstSignal}, shutting down...`);
    if (watcher) {
      try {
        await watcher.close();
      } catch (err) {
        logger.warn(`watcher.close() failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    // Close every server in parallel, then dispose every (possibly hot-
    // reload-swapped) pool. Each pool's dispose() waits for in-flight
    // requests to drain; running them in parallel is the right shape
    // even for N servers because shutdown is signalled to all at once.
    //
    // WebSocket-server shutdown layers on top: each
    // `AttachedWebSocketServer.close()` sends close-frame 1001 (going
    // away) to every live socket BEFORE we kill the underlying http
    // server, so the client sees a clean close instead of a
    // mid-frame disconnect.
    await Promise.allSettled(
      wsServers.map(async (ws) => {
        try {
          await ws.attached.close();
        } catch (err) {
          logger.warn(
            `WebSocket close() failed for ${ws.api.apiLogicalId}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })
    );
    await Promise.allSettled(
      servers.map(async ({ server, group }) => {
        try {
          await server.close();
        } catch (err) {
          logger.warn(
            `server.close() failed for ${group.displayName}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })
    );
    await Promise.allSettled(
      wsServers.map(async (ws) => {
        try {
          await ws.server.close();
        } catch (err) {
          logger.warn(
            `WebSocket server.close() failed for ${ws.api.apiLogicalId}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })
    );
    await Promise.allSettled(
      servers.map(async ({ server, group }) => {
        try {
          await server.getServerState().pool.dispose();
        } catch (err) {
          logger.warn(
            `pool.dispose() failed for ${group.displayName}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })
    );
    await Promise.allSettled(
      wsServers.map(async (ws) => {
        try {
          await ws.server.getServerState().pool.dispose();
        } catch (err) {
          logger.warn(
            `WebSocket pool.dispose() failed for ${ws.api.apiLogicalId}: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })
    );
    // Remove every tmpdir we materialized inline `Code.ZipFile` Lambdas
    // into. Each is `mkdtempSync(...)` under the OS tmpdir, so the only
    // owner of cleanup is this process. Best-effort: log + continue on
    // any per-dir failure.
    for (const dir of inlineTmpDirs) {
      try {
        rmSync(dir, { recursive: true, force: true });
      } catch (err) {
        logger.warn(
          `Failed to remove inline-code tmpdir ${dir}: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
    for (const dir of layerTmpDirs) {
      try {
        rmSync(dir, { recursive: true, force: true });
      } catch (err) {
        logger.warn(
          `Failed to remove merged-layers tmpdir ${dir}: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
    // Dispose the synthesized profile credentials file (one INI file under
    // `/tmp/cdk-local-profile-creds-*/`). Leaving temp credential files on
    // disk after process exit is a security smell; `dispose()` rm's the
    // tempdir recursively + force, so re-entrant calls (signal-mid-finally
    // race) are safe.
    if (profileCredsFile) {
      try {
        await profileCredsFile.dispose();
      } catch (err) {
        logger.warn(
          `Failed to remove profile credentials tmpdir ${profileCredsFile.hostPath}: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
  });
  const shutdown = async (signal: string, exitCode: number): Promise<void> => {
    if (shutdownStarted) {
      if (!forceExitArmed) {
        forceExitArmed = true;
        logger.warn(
          `Received second ${signal}; force-exiting. Orphan containers may remain — run 'docker ps --filter name=${getEmbedConfig().resourceNamePrefix}-' and 'docker rm -f' to clean up.`
        );
        process.exit(130);
      }
      return;
    }
    shutdownStarted = true;
    firstSignal = signal;
    firstExitCode = exitCode;
    await runCleanup();
    process.exit(firstExitCode);
  };

  process.on('SIGINT', () => {
    void shutdown('SIGINT', 130);
  });
  process.on('SIGTERM', () => {
    void shutdown('SIGTERM', 0);
  });
  process.on('uncaughtException', (err) => {
    logger.error(
      `Uncaught exception: ${err instanceof Error ? (err.stack ?? err.message) : String(err)}`
    );
    void shutdown('uncaughtException', 1);
  });
  process.on('unhandledRejection', (reason) => {
    logger.error(
      `Unhandled rejection: ${reason instanceof Error ? (reason.stack ?? reason.message) : String(reason)}`
    );
    void shutdown('unhandledRejection', 1);
  });

  // Block forever — the signal handlers exit the process.
  await new Promise<never>(() => undefined);
}

/** The `--watch` file-watcher predicates + the resolved exclude list. */
export interface WatchPredicates {
  /** chokidar `ignored` predicate (exclude-only; prunes dirs + files). */
  ignored: (absPath: string) => boolean;
  /** Per-event include gate — exclude wins, then include. */
  shouldTrigger: (absPath: string) => boolean;
  /** Resolved exclude glob list (mandatory + cdk.json), for logging. */
  excludePatterns: string[];
}

/**
 * Build the `--watch` file-watcher predicates for a source tree rooted
 * at `watchRoot` (the synth working directory).
 *
 * The synth output directory is always excluded so the reload's own
 * re-synth writes never re-trigger the watcher (no loop); `node_modules`
 * / `.git` are excluded for traversal cost. `cdk.json` `watch.exclude`
 * globs layer on top, and `watch.include` gates which changes fire a
 * reload. The output dir is only added as a glob when it lives UNDER the
 * watch root — when it equals the root (`''`) or sits outside it
 * (`..`-prefixed), a watcher rooted at `watchRoot` never traverses it,
 * so no exclude entry is needed (and a `''` / `..` glob is meaningless).
 *
 * `@internal` exported for unit tests (the exclude/include composition +
 * the output-dir relativization edge cases).
 */
export function createWatchPredicates(args: {
  watchRoot: string;
  output: string;
  watchConfig: CdkWatchConfig;
}): WatchPredicates {
  const { watchRoot, output, watchConfig } = args;
  const toRel = (abs: string): string => path.relative(watchRoot, abs).split(path.sep).join('/');
  // `path.relative` yields `''` when the output dir IS the watch root and a
  // `..`-prefixed path when it is outside — in both cases a watcher rooted
  // at `watchRoot` never traverses it, so it needs no exclude glob.
  const outputRel = toRel(path.resolve(watchRoot, output));
  const mandatoryExcludes = outputRel !== '' && !outputRel.startsWith('..') ? [outputRel] : [];
  const excludePatterns = [...mandatoryExcludes, 'node_modules', '.git', ...watchConfig.exclude];
  const excludeMatcher = createGlobMatcher(excludePatterns);
  const includeMatcher = createGlobMatcher(watchConfig.include);
  const ignored = (absPath: string): boolean => {
    const rel = toRel(absPath);
    if (rel === '') return false; // the watch root itself
    if (rel.startsWith('..')) return true; // outside the root
    return excludeMatcher(rel);
  };
  const shouldTrigger = (absPath: string): boolean => {
    const rel = toRel(absPath);
    if (rel === '' || rel.startsWith('..')) return false;
    if (excludeMatcher(rel)) return false;
    return includeMatcher(rel);
  };
  return { ignored, shouldTrigger, excludePatterns };
}

/**
 * Match the `--stack` pattern (or single-stack auto-detect) to a list
 * of stacks the route-discovery walks. Mirrors the deploy/diff matcher
 * routing rules.
 */
/** @internal exported for unit tests. */
export function pickTargetStacks(
  stacks: StackInfo[],
  pattern: string | undefined,
  cfnStackFallback?: string,
  targetFallback?: string,
  allStacks?: boolean
): StackInfo[] {
  // Issue #55: `--all-stacks` serves the union of every stack's APIs.
  // It is mutually exclusive with the selectors below (validated by the
  // caller), so when set we skip the resolution chain entirely and
  // return every stack. The downstream server grouping disambiguates
  // same-logical-id collisions across stacks, so a union boots fine.
  if (allStacks) return stacks;

  // Resolution chain (first non-empty wins):
  //   1. `--stack <pattern>`                            (explicit)
  //   2. `--from-cfn-stack <explicit-name>`             (PR #44)
  //   3. positional target's stack-name prefix          (PR #45)
  //      e.g. `cdkl start-api MyStack/MyApi` infers `MyStack`.
  //
  // CDK apps typically deploy each synth stack under its own stack-name,
  // so any of the three values identifies the synth target unambiguously
  // without the user having to repeat themselves via `--stack`.
  const effective = pattern ?? cfnStackFallback ?? targetFallback;
  if (effective) {
    return matchStacks(stacks, [effective]);
  }
  if (stacks.length === 1) return stacks;
  if (stacks.length === 0) return [];
  // Multi-stack apps are served as a union only on explicit opt-in
  // (`--all-stacks`); otherwise we require an explicit single-target
  // selection so users don't accidentally serve (and boot Docker
  // containers for) a side-stack's API.
  throw new Error(
    `Multi-stack app: pass --stack <name>, --from-cfn-stack <name>, a stack-qualified target like "<StackName>/<construct>", or --all-stacks to serve every stack. Available stacks: ${stacks.map((s) => s.stackName).join(', ')}.`
  );
}

/**
 * Issue #55: `--all-stacks` serves every stack's API as a union, so it
 * cannot be combined with a selector that names a target subset.
 * Returns the human-readable list of conflicting selectors (empty when
 * `--all-stacks` is off or there is no conflict).
 *
 * The bare `--from-cfn-stack` flag (Commander maps it to boolean `true`)
 * is NOT a conflict — it binds each routed stack to its own CFn stack,
 * which is exactly the multi-stack union case. Only an explicit
 * `--from-cfn-stack <name>` (a string) names a single stack and conflicts.
 *
 * Extracted as a pure function so it can be unit-tested without booting
 * the full server.
 *
 * @internal exported for unit tests.
 */
export function allStacksConflicts(
  options: Pick<LocalStartApiOptions, 'allStacks' | 'stack' | 'api' | 'fromCfnStack'>,
  targets: readonly string[],
  apiFilters: readonly string[]
): string[] {
  if (!options.allStacks) return [];
  const conflicts: string[] = [];
  if (apiFilters.length > 0) {
    conflicts.push(
      targets.length > 0 ? `target(s) [${targets.join(', ')}]` : `--api '${options.api}'`
    );
  }
  if (options.stack !== undefined) conflicts.push(`--stack '${options.stack}'`);
  if (typeof options.fromCfnStack === 'string') {
    conflicts.push(`--from-cfn-stack '${options.fromCfnStack}'`);
  }
  return conflicts;
}

/**
 * Result of {@link resolveApiTargetSubset}: the filtered route union plus
 * the list of identifiers that matched nothing (so the caller can warn
 * once per typo without re-running the per-id filter).
 */
export interface ApiTargetSubset {
  /** The UNION of routes matching any supplied identifier. */
  readonly filtered: RouteWithAuth[];
  /**
   * The UNION of WebSocket APIs matching any supplied identifier (issue
   * #311). Empty when no WS API was passed / matched. At least one of
   * `filtered` / `filteredWebSocketApis` is non-empty (else the resolver
   * throws "did not match any discovered API").
   */
  readonly filteredWebSocketApis: DiscoveredWebSocketApi[];
  /** Identifiers that matched zero routes AND zero WS APIs — input order. */
  readonly unmatched: string[];
}

/**
 * Resolve the variadic `cdkl start-api <target...>` subset against the
 * discovered route surface — the pure core of the subset-serving path.
 *
 * Behavior:
 *  - Rejects a BARE logical id (no `:`, no `/`) when the app has more than
 *    one stack: a bare id is ambiguous because two stacks can carry the
 *    same logical id (mirrors `cdkl invoke` / `cdkl run-task`'s resolver).
 *    THROWS with the disambiguation hint.
 *  - Filters routes to the UNION of the identifiers via
 *    {@link filterRoutesByApiIdentifiers}. THROWS when the union is empty
 *    (no identifier matched anything), listing the available identifiers.
 *  - Returns the surviving union in `filtered` and the identifiers that
 *    matched nothing in `unmatched`. A single typo among valid ids keeps
 *    the siblings in `filtered` and surfaces the typo via `unmatched`;
 *    the caller is responsible for warning (so the warning can be gated
 *    one-shot across `--watch` reloads — see the call site).
 *
 * `availableApiIdentifiers(routes)` is computed ONCE here (not per-id) so
 * the resolution is O(N·M) rather than O(N²·M).
 *
 * @internal exported for unit tests + library consumers (re-exported from
 * the package entry).
 */
export function resolveApiTargetSubset(
  routes: readonly RouteWithAuth[],
  identifiers: readonly string[],
  stackNames: readonly string[],
  webSocketApis: readonly DiscoveredWebSocketApi[] = []
): ApiTargetSubset {
  // Strict multi-stack rejection for the bare-logical-id form (no `:`,
  // no `/`). A bare id in a multi-stack app is ambiguous, because two
  // stacks can legitimately have the same logical id — pre-PR the filter
  // would silently match both and collide in `groupRoutesByServer`'s
  // serverKey. We reject upfront with the same disambiguation hint
  // `cdkl invoke` uses.
  if (stackNames.length > 1) {
    for (const id of identifiers) {
      const isBareId = !id.includes(':') && !id.includes('/');
      if (isBareId) {
        throw new Error(
          `Multiple stacks in app, target '${id}' is missing a stack prefix. ` +
            `Use 'StackName:${id}' or 'StackName/${id}' (Construct path form). ` +
            `Available stacks: ${stackNames.join(', ')}.`
        );
      }
    }
  }

  const filtered = filterRoutesByApiIdentifiers(routes, identifiers);
  // Issue #311: WebSocket APIs are filtered by the SAME identifiers as routes
  // (previously they were always served as a group, so an explicit target
  // that named a WS API matched no route and threw). A WS-only target now
  // resolves to its API, and an HTTP / REST target no longer drags unrelated
  // WS APIs along.
  const filteredWebSocketApis = filterWebSocketApisByIdentifiers(webSocketApis, identifiers);
  if (filtered.length === 0 && filteredWebSocketApis.length === 0) {
    const available =
      [...availableApiIdentifiers(routes), ...availableWebSocketApiIdentifiers(webSocketApis)].join(
        ', '
      ) || '(none)';
    throw new Error(
      `Target(s) [${identifiers.join(', ')}] did not match any discovered API. Available identifiers: ${available}.`
    );
  }

  // Surface any individual identifier that matched nothing — neither a route
  // NOR a WS API (the union still has the matched siblings, so the run
  // continues) — a single typo in a multi-target list otherwise silently
  // serves a smaller set than the user asked for.
  const unmatched = identifiers.filter(
    (id) =>
      filterRoutesByApiIdentifiers(routes, [id]).length === 0 &&
      filterWebSocketApisByIdentifiers(webSocketApis, [id]).length === 0
  );

  return { filtered, filteredWebSocketApis, unmatched };
}

/**
 * Derive the single synth stack prefix shared by every supplied target,
 * for the synth-scope optimization (item 5 of the start-api subset UX).
 *
 * Each target's stack prefix is the segment before its first `/` (the
 * `<StackName>/<construct>` Construct-path form). Returns:
 *  - `undefined` when `targets` is empty (serve-all path — no prefix).
 *  - `undefined` when ANY target lacks a `/` (bare logical id — the synth
 *    target can't be inferred from it, so fall back to the existing
 *    single-stack auto-pick / multi-stack rejection path).
 *  - `undefined` when targets span MORE THAN ONE distinct prefix (the
 *    subset crosses stacks, so synth must cover all of them — see
 *    {@link shouldSynthAllStacks}).
 *  - the shared prefix when every target carries the SAME `<StackName>/`
 *    prefix — keeping the single-stack synth optimization.
 *
 * @internal exported for unit tests.
 */
export function deriveSynthStackPrefix(targets: readonly string[]): string | undefined {
  if (targets.length === 0) return undefined;
  const prefixes = new Set<string>();
  for (const t of targets) {
    const slash = t.indexOf('/');
    if (slash === -1) return undefined; // bare logical id — can't infer a stack
    prefixes.add(t.slice(0, slash));
  }
  return prefixes.size === 1 ? [...prefixes][0] : undefined;
}

/**
 * Decide whether synth should cover ALL stacks for the given target
 * subset (item 5). Synth is scoped to one stack only when a single stack
 * can be pinned; otherwise we synth every stack and the union is filtered
 * down by `apiFilters` afterwards.
 *
 * Returns `true` (synth all) when there ARE explicit targets but no
 * single stack can be pinned from them — i.e. `--stack` is unset, an
 * explicit `--from-cfn-stack <name>` is unset, AND
 * {@link deriveSynthStackPrefix} can't resolve one shared prefix (targets
 * span stacks, or any target is a bare logical id). Returns `false` when
 * there are no targets (serve-all already synths everything via the
 * regular path) or a stack is otherwise pinned.
 *
 * @internal exported for unit tests.
 */
export function shouldSynthAllStacks(
  targets: readonly string[],
  stackPattern: string | undefined,
  cfnStackFallback: string | undefined
): boolean {
  if (targets.length === 0) return false;
  if (stackPattern !== undefined || cfnStackFallback !== undefined) return false;
  return deriveSynthStackPrefix(targets) === undefined;
}

/**
 * Decide whether bare `start-api` should open the pre-selected-all
 * multi-select picker. The picker runs ONLY when:
 *  - no explicit API subset was named (`apiFilters` empty — neither
 *    positional `<targets...>` nor the deprecated `--api`),
 *  - `--all-stacks` was not passed (that path already serves every API),
 *  - AND stdin/stdout is a TTY.
 *
 * In a non-TTY (CI / pipe) this returns `false`, and the caller serves
 * EVERY discovered API without prompting — start-api's one intentional
 * asymmetry vs invoke / run-task (which error when bare in a non-TTY),
 * because start-api legitimately has a "serve all" default.
 *
 * Extracted as a pure function (TTY is passed in) so the decision is
 * unit-testable without booting the server or juggling global TTY state.
 *
 * @internal exported for unit tests.
 */
export function shouldPromptBareMultiSelect(
  apiFilters: readonly string[],
  allStacks: boolean | undefined,
  isTty: boolean
): boolean {
  return apiFilters.length === 0 && !allStacks && isTty;
}

/**
 * Decide whether the `--from-cfn-stack <name>` redundancy tip should
 * fire for the current invocation. Fires only when:
 *  - `fromCfnStack` is a non-empty STRING (explicit value, not bare `true`)
 *  - exactly ONE stack is routed
 *  - the explicit value equals the routed stack's `stackName`
 *
 * Extracted as a pure function so it can be unit-tested without booting
 * the full server. See improvement A in the start-api UX PR.
 *
 * @internal exported for unit tests.
 */
export function shouldEmitFromCfnRedundancyTip(
  fromCfnStack: string | boolean | undefined,
  routedStackNames: readonly string[]
): boolean {
  if (typeof fromCfnStack !== 'string') return false;
  if (fromCfnStack.length === 0) return false;
  if (routedStackNames.length !== 1) return false;
  return fromCfnStack === routedStackNames[0];
}

/**
 * One-shot wrapper around `shouldEmitFromCfnRedundancyTip` for the
 * `--watch` hot-reload path. `synthesizeAndBuild` re-runs on every
 * reload firing, so without a gate the tip would re-emit on every
 * reload — noisy. This helper consults the caller-supplied ref:
 *  - If the predicate fires AND the ref is still `false`, calls `emit`
 *    and flips the ref to `true`.
 *  - On subsequent invocations the ref is `true` and the helper is a
 *    no-op for the rest of the ref's lifetime.
 *  - When the predicate does NOT fire (no `--from-cfn-stack` value /
 *    intentionally-different value / multi-stack run), the ref stays
 *    `false` so a future reload whose synthesized stacks change in a
 *    way that DOES make the value redundant still emits the tip once.
 *
 * The ref is owned by `localStartApiCommand` (one per server boot), so
 * independent server invocations get independent flags.
 *
 * @internal exported for unit tests.
 */
export function tryEmitFromCfnRedundancyTipOnce(
  fromCfnStack: string | boolean | undefined,
  routedStackNames: readonly string[],
  emittedRef: { value: boolean },
  emit: (routedStackName: string) => void
): void {
  if (emittedRef.value) return;
  if (!shouldEmitFromCfnRedundancyTip(fromCfnStack, routedStackNames)) return;
  const routedStackName = routedStackNames[0];
  if (routedStackName === undefined) return;
  emit(routedStackName);
  emittedRef.value = true;
}

/**
 * Distinct, stable list of Lambda logical IDs reachable through any
 * discovered route OR referenced by a Lambda authorizer attached to one
 * of those routes. Stable order = first-occurrence order in the routes
 * list, then any newly-introduced authorizer Lambdas, which keeps the
 * route-table output deterministic.
 */
function uniqueLambdaIds(
  routes: readonly DiscoveredRoute[],
  routesWithAuth: readonly RouteWithAuth[],
  webSocketApis: readonly DiscoveredWebSocketApi[] = []
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of routes) {
    // Skip deferred-error unsupported routes, synthetic mockCors
    // routes, and service-integration routes — none of them dispatch
    // to a Lambda, so spinning up their containers (when a
    // lambdaLogicalId IS attached, e.g. on a Function URL with
    // AuthType=AWS_IAM) would be wasted boot time.
    if (r.unsupported || r.mockCors || r.serviceIntegration) continue;
    if (r.lambdaLogicalId.length === 0) continue;
    if (!seen.has(r.lambdaLogicalId)) {
      seen.add(r.lambdaLogicalId);
      out.push(r.lambdaLogicalId);
    }
  }
  for (const entry of routesWithAuth) {
    // An unsupported / mockCors / service-integration route never
    // reaches the authorizer pass, so its authorizer Lambda doesn't
    // need a container either.
    if (entry.route.unsupported || entry.route.mockCors || entry.route.serviceIntegration) {
      continue;
    }
    const auth = entry.authorizer;
    if (!auth) continue;
    if (auth.kind === 'lambda-token' || auth.kind === 'lambda-request') {
      if (!seen.has(auth.lambdaLogicalId)) {
        seen.add(auth.lambdaLogicalId);
        out.push(auth.lambdaLogicalId);
      }
    }
  }
  // #462: pull every WebSocket route's backing Lambda into the
  // unified spec map so the shared container pool can dispatch them
  // alongside HTTP / REST routes.
  for (const api of webSocketApis) {
    for (const r of api.routes) {
      if (!seen.has(r.targetLambdaLogicalId)) {
        seen.add(r.targetLambdaLogicalId);
        out.push(r.targetLambdaLogicalId);
      }
    }
  }
  return out;
}

/**
 * Prefetch the JWKS for every Cognito / JWT authorizer attached to a
 * discovered route. Failures degrade to pass-through mode (verifier
 * surfaces a warn line on first hit); we still issue the prefetch so
 * the warn lands at startup rather than mid-request.
 */
async function prewarmJwks(
  routesWithAuth: readonly RouteWithAuth[],
  jwksCache: import('../../local/cognito-jwt.js').JwksCache
): Promise<void> {
  const urls = new Set<string>();
  for (const entry of routesWithAuth) {
    const auth = entry.authorizer;
    if (!auth) continue;
    if (auth.kind === 'cognito') {
      // Multi-pool federation: pre-warm JWKS for EVERY pool in
      // ProviderARNs[], so issuer-matched verification at request time
      // doesn't pay a cold-start latency on the first request to each
      // tenant's pool.
      for (const pool of auth.pools) {
        urls.add(buildCognitoJwksUrl(pool.region, pool.userPoolId));
      }
    } else if (auth.kind === 'jwt') {
      const url =
        auth.region && auth.userPoolId
          ? buildCognitoJwksUrl(auth.region, auth.userPoolId)
          : buildJwksUrlFromIssuer(auth.issuer);
      urls.add(url);
    }
  }
  await Promise.all([...urls].map((u) => jwksCache.fetchAndCache(u)));
}

/**
 * Emit a one-line warn for every VPC-config Lambda. The handler still
 * runs locally, but its container does not get attached to the AWS
 * VPC's subnets — calls to private RDS / ElastiCache will fail. cdk-local
 * surfaces this so the developer can pin the unexpected behavior to
 * the VPC config rather than chasing a "connection refused" rabbit
 * hole.
 */
function warnVpcConfigLambdas(
  routesWithAuth: readonly RouteWithAuth[],
  stacks: readonly StackInfo[]
): void {
  const logger = getLogger();
  // Walk every reachable Lambda (route handler + authorizer) once.
  const seen = new Set<string>();
  const reachable: string[] = [];
  for (const entry of routesWithAuth) {
    if (!seen.has(entry.route.lambdaLogicalId)) {
      seen.add(entry.route.lambdaLogicalId);
      reachable.push(entry.route.lambdaLogicalId);
    }
    const auth: AuthorizerInfo | undefined = entry.authorizer;
    if (auth && (auth.kind === 'lambda-token' || auth.kind === 'lambda-request')) {
      if (!seen.has(auth.lambdaLogicalId)) {
        seen.add(auth.lambdaLogicalId);
        reachable.push(auth.lambdaLogicalId);
      }
    }
  }
  for (const logicalId of reachable) {
    for (const stack of stacks) {
      const resource = stack.template.Resources?.[logicalId];
      if (!resource || resource.Type !== 'AWS::Lambda::Function') continue;
      const props = resource.Properties ?? {};
      const vpcConfig = props['VpcConfig'];
      if (vpcConfig && typeof vpcConfig === 'object' && Object.keys(vpcConfig).length > 0) {
        logger.warn(
          `Lambda ${logicalId} has VpcConfig — local container will reach external services via the host's network, NOT through the deployed VPC's NAT/private subnets. Calls to private RDS/ElastiCache will fail.`
        );
      }
      break;
    }
  }
}

/**
 * Walk the discovered routes for `AuthorizationType: 'AWS_IAM'` and emit
 * a one-line warn naming the affected routes. Returns `true` when at
 * least one IAM route is present so the caller wires the SigV4
 * credentials loader. Re-runs across hot reloads are silent — the warn
 * fires only at initial boot (matches `warnVpcConfigLambdas`'s policy).
 *
 * Implementation note: signature verification only — IAM policy
 * evaluation (resource / action / condition) is NOT emulated. See
 * `src/local/sigv4-verify.ts` and the help text in `docs/cli-reference.md`.
 */
function warnIamRoutes(routesWithAuth: readonly RouteWithAuth[]): boolean {
  const logger = getLogger();
  const iamRoutes: string[] = [];
  const oacRoutes: string[] = [];
  for (const entry of routesWithAuth) {
    if (entry.authorizer?.kind !== 'iam') continue;
    // OAC-fronted Function URLs are reported separately: their SigV4 check
    // is auto-relaxed (CloudFront signs origin requests in production), so
    // the generic "verifies signatures against your local credentials" line
    // would mislead.
    if (entry.authorizer.oacFronted === true) {
      oacRoutes.push(entry.route.declaredAt);
    } else {
      iamRoutes.push(entry.route.declaredAt);
    }
  }
  if (iamRoutes.length === 0 && oacRoutes.length === 0) return false;
  if (iamRoutes.length > 0) {
    logger.warn(
      `${iamRoutes.length} route(s) declare AuthorizationType: AWS_IAM — ${getEmbedConfig().cliName} start-api ` +
        `verifies the SigV4 signatures it CAN (requests signed with your local AWS credentials), but ` +
        `cannot verify a federated / Cognito Identity Pool / cross-account signer and does NOT emulate ` +
        `IAM policy evaluation (resource / action / condition rules). By default such unverifiable ` +
        `requests warn-and-pass with a placeholder principalId — pass --strict-sigv4 to deny them ` +
        `instead. Downstream authorization is the dev's responsibility.`
    );
    for (const declaredAt of iamRoutes) {
      logger.warn(`  - ${declaredAt}`);
    }
  }
  if (oacRoutes.length > 0) {
    logger.warn(
      `${oacRoutes.length} Function URL route(s) with AuthType: AWS_IAM are fronted by a CloudFront ` +
        `Origin Access Control. In production CloudFront re-signs the origin request, so no local ` +
        `client signature can be verified — ${getEmbedConfig().cliName} start-api passes these through (warn-and-pass) ` +
        `and they ignore --strict-sigv4. Do NOT trust the request identity in handler code.`
    );
    for (const declaredAt of oacRoutes) {
      logger.warn(`  - ${declaredAt}`);
    }
  }
  return true;
}

/**
 * Resolve the effective IAM role ARN to assume for a single routed
 * Lambda, honoring the four `--assume-role` forms of issue #256
 * Option 1 (non-breaking step):
 *
 *   - per-Lambda override (`<LogicalId>=<arn>`) — wins over every other
 *     form for the named Lambda.
 *   - global default (bare ARN) — used when the per-Lambda map misses.
 *   - bare-auto-resolve (`--assume-role` with no value) — when neither
 *     the per-Lambda map nor a global default applies, resolves THIS
 *     Lambda's own `Role` (its `AWS::Lambda::Function`'s `Role`
 *     property -> role ARN). Resolution sources, in order:
 *       1. literal-ARN form of `Properties.Role` in the SYNTHESIZED
 *          template (rare in CDK output but possible);
 *       2. deployed state (`stateBundle.state.resources`) when
 *          `--from-cfn-stack` (or a host-provided extension) is
 *          active, via the same shared helper
 *          `resolveExecutionRoleArnFromState` `cdkl invoke`'s bare-
 *          form uses.
 *   - flag absent / `--no-assume-role-auto` — returns `undefined`
 *     (per-Lambda map and global default already filtered out by the
 *     null-check at the top; bare-auto-resolve = false short-circuits
 *     before the state lookup).
 *
 * The shape-level mutual exclusion (bare-auto-resolve + global ARN
 * together) is caught at boot by
 * {@link normalizeStartApiAssumeRole}; this resolver only sees a
 * well-formed `AssumeRoleOption`.
 *
 * Bare-auto-resolve misses (no literal-ARN, no state, state did not
 * carry the role attribute) warn-and-fall-through to the
 * dev-creds-passed-through branch — same trade-off `cdkl invoke
 * --assume-role` makes when bare auto-resolve cannot find an ARN.
 *
 * Exported for unit testing.
 */
export function resolveStartApiAssumeRoleArn(args: {
  logicalId: string;
  assumeRole: AssumeRoleOption | undefined;
  lambdaResource: TemplateResource;
  stateBundle: StackStateBundle | undefined;
}): string | undefined {
  const { logicalId, assumeRole, lambdaResource, stateBundle } = args;
  if (!assumeRole) return undefined;

  // Per-Lambda override > global default > bare-auto-resolve.
  // `effectiveAssumeRoleArn` already handles the first two.
  const explicit = effectiveAssumeRoleArn(logicalId, assumeRole);
  if (explicit) return explicit;

  if (!assumeRole.bareAutoResolve) return undefined;

  // Bare-auto-resolve: try the synthesized template's literal-ARN
  // `Properties.Role` first (most CDK apps render this as an intrinsic,
  // but explicit-ARN refs DO surface here), then the deployed state.
  const roleProp = (lambdaResource.Properties ?? {})['Role'];
  if (typeof roleProp === 'string') {
    // Issue #607: shape-checked, not `startsWith('arn:')`. This value is
    // returned to a caller that hands it to `AssumeRoleCommand.RoleArn`. The
    // send itself is bounded at the choke point in `utils/role-arn.ts`; the
    // check HERE is what tells the user WHERE the bad value came from, which a
    // refusal at the send cannot.
    if (isIamRoleArn(roleProp)) {
      return roleProp;
    }
    // Warn, then FALL THROUGH to the state lookup — the pre-#607 control flow
    // for a string that failed the check, preserved deliberately: a template
    // Role that is not a literal ARN was always meant to be recoverable from
    // deployed state.
    getLogger().warn(
      `--assume-role: the template Role for '${logicalId}' is not a well-formed IAM role ARN: ${describeRejectedRoleArn(roleProp)}. Ignoring it.`
    );
  }
  if (stateBundle) {
    const fromState = resolveExecutionRoleArnFromState(stateBundle.state, logicalId);
    if (fromState) {
      // Issue #570: FLATTENED. Under `--from-cfn-stack` the state record is
      // built from CloudFormation responses, so this ARN is wire-derived and
      // `info` is a default level studio mirrors into an HTTP-served ring.
      getLogger().info(
        `--assume-role: auto-resolved execution role for '${logicalId}' from state: ${flattenToOneLine(
          fromState
        )}`
      );
      return fromState;
    }
  }

  // Miss: warn-and-fall-through so the user sees why the container is
  // getting dev creds instead of the deployed function's narrow role.
  // The same shape `cdkl invoke --assume-role` produces when state /
  // template ARN can't recover the role.
  getLogger().warn(
    `--assume-role: could not auto-resolve the execution role ARN for '${logicalId}'. ` +
      `Pair --assume-role with --from-cfn-stack (or a host-provided state-source extension) ` +
      `so the deployed Role's ARN can be looked up, OR pin the ARN explicitly with ` +
      `--assume-role ${logicalId}=<arn>. ` +
      "Falling back to the developer's shell credentials for this Lambda."
  );
  return undefined;
}

/**
 * Build the per-Lambda container spec — code dir, env vars (template +
 * --env-vars overlay), STS-issued creds when --assume-role names this
 * Lambda, optional --debug-port reservation. Errors out with a clear
 * message if the Lambda's code can't be resolved (asset directory
 * missing, runtime not supported).
 */
async function buildContainerSpec(args: {
  logicalId: string;
  stacks: StackInfo[];
  overrides: EnvOverrideFile | undefined;
  assumeRole: AssumeRoleOption | undefined;
  containerHost: string;
  debugPort?: number;
  stsRegion: string | undefined;
  /**
   * The caller's set of materialized inline-code tmpdirs. Every dir
   * `materializeInlineCode` returns is also pushed here so the graceful
   * shutdown path can remove it. The set is shared across all calls
   * within one server boot.
   */
  inlineTmpDirs: Set<string>;
  /**
   * The caller's set of merged-layers tmpdirs (PR 6 of #224, issue
   * #232). Every multi-layer Lambda's `materializeLambdaLayers(...)`
   * call records its merged tmpdir here so `shutdown(...)` can remove
   * each one. Single-layer Lambdas bind-mount the layer's asset dir
   * directly and never write into this set.
   */
  layerTmpDirs: Set<string>;
  /**
   * `--from-state` substitution input keyed by stack name. Empty when
   * the flag is unset OR when no routed stack had loadable state. Per-
   * stack entries supply state.resources + the pseudo-parameter bag
   * the env-resolver consults for `Ref AWS::*` / `${AWS::*}` placeholders.
   */
  stateByStack: Map<string, StackStateBundle>;
  /**
   * `--no-pull` flag, threaded to the IMAGE branch's ECR-pull fallback
   * (`pullEcrImage(... {skipPull})`). ZIP branch ignores this — the
   * base-image pull happens once up the call chain
   * (`synthesizeAndBuild`'s `pullImage` loop) and is independently
   * gated by the same flag.
   */
  skipPull: boolean;
  /**
   * Issue #249 / C8 — `--no-build` flag, threaded to the IMAGE
   * branch's local-asset build path. ZIP branch ignores. Short-circuits
   * `docker build` when the deterministic content-addressed tag is
   * already in the local registry; errors when missing.
   */
  skipBuild?: boolean;
  /**
   * Issue #249 / C7 — `--ecr-role-arn` flag, threaded to the IMAGE
   * branch's ECR-pull fallback for cross-account / centralized
   * registry pulls. Undefined when the flag is unset.
   */
  ecrRoleArn?: string;
  /**
   * The CLI's `--profile`, forwarded to the IMAGE branch's ECR-pull
   * fallback so `ecr:GetAuthorizationToken` authenticates as the
   * profile's account (matching the ECS path). Undefined when
   * `--profile` is unset.
   */
  profile?: string;
  /**
   * Issue #448: optional `--layer-role-arn` value. Forwarded into
   * {@link materializeLambdaLayers}, which `sts:AssumeRole`s into this
   * role before calling `lambda:GetLayerVersion` for every literal-ARN
   * entry. Same role applies to every routed Lambda — if the user's
   * apps reference layers in multiple accounts they need a single
   * cross-account role that can read all of them.
   */
  layerRoleArn?: string;
  /**
   * Issue #654: profile-resolved credentials to forward into the Lambda
   * container's env when `--profile <p>` is set AND this Lambda has no
   * `--assume-role` mapping. Resolved once at server boot via
   * `resolveProfileCredentials` and shared across every spec build.
   * Undefined when `--profile` is unset (the pre-fix path stays in
   * effect — `forwardAwsEnv` copies `process.env.AWS_*`).
   */
  profileCredentials?: { accessKeyId: string; secretAccessKey: string; sessionToken?: string };
  /**
   * When set, a synthesized AWS shared credentials file (one INI section,
   * the resolved
   * `[<profile-name>]` block) the container should bind-mount + reference
   * via `AWS_SHARED_CREDENTIALS_FILE` + `AWS_PROFILE` env vars. Lets
   * handlers that use `fromIni({ profile })` explicitly inside their
   * code resolve to the same creds locally. Undefined when `--profile`
   * was not passed.
   */
  profileCredsFile?: ProfileCredentialsFile;
  /**
   * `--profile`'s configured region (`~/.aws/config`'s `region =` for the
   * profile), resolved once at server boot via
   * {@link resolveProfileCredentials}. Lowest-precedence fallback for the
   * container's `AWS_REGION` — used only when no explicit region was
   * injected. Undefined when `--profile` is unset or the profile has no
   * region.
   */
  profileRegion?: string | undefined;
  /**
   * `--stack-region` flag value. Highest-precedence fallback for the
   * container's `AWS_REGION` (it also drives the `--from-cfn-stack` CFn
   * client, so the container reaches the same region as the resources it
   * is bound to). Undefined when the flag is unset.
   */
  stackRegionOverride?: string | undefined;
}): Promise<ContainerSpec> {
  const {
    logicalId,
    stacks,
    overrides,
    assumeRole,
    containerHost,
    debugPort,
    stsRegion,
    inlineTmpDirs,
    layerTmpDirs,
    stateByStack,
    skipPull,
    profile,
    layerRoleArn,
    profileCredentials,
    profileCredsFile,
    profileRegion,
    stackRegionOverride,
  } = args;
  const lambda = resolveLambdaByLogicalId(logicalId, stacks);

  // ZIP / IMAGE divergence (closes #453). ZIP needs `codeDir` (bind-
  // mount source for /var/task) + `optDir` (bind-mount source for
  // /opt from same-stack Layers). IMAGE needs a pre-built local
  // docker image tag — resolved here once at server boot, then the
  // container pool runs `docker run` against it without further
  // resolution. Layers are silently ignored on the IMAGE branch
  // (matches AWS's invoke-time behavior: container Lambdas can't
  // declare Layers).
  let codeDir: string | undefined;
  let optDir: string | undefined;
  let imageRef: string | undefined;
  let platform: string | undefined;
  if (lambda.kind === 'zip') {
    // Re-use `cdkl invoke`'s materialization rules: inline (Code.ZipFile)
    // Lambdas are written to a temp dir, and an asset-backed codePath is a
    // directory OR a `.zip` file (the latter extracted by materializeAssetCodeDir).
    if (lambda.codePath) {
      // A `.zip`-packaged asset (`Code.fromAsset('bundle.zip')` / a bundling
      // that emits a zip) is extracted to a temp dir for the bind-mount; an
      // already-unzipped asset dir passes through. The temp dir is tracked in
      // `inlineTmpDirs` so the same shutdown path removes it.
      const materialized = materializeAssetCodeDir(lambda.codePath);
      codeDir = materialized.dir;
      if (materialized.tmpDir) inlineTmpDirs.add(materialized.tmpDir);
    } else {
      codeDir = materializeInlineCode(
        lambda.handler,
        lambda.inlineCode ?? '',
        resolveRuntimeFileExtension(lambda.runtime),
        inlineTmpDirs
      );
    }
    // NOTE: the ZIP warm container's `--platform` is derived from
    // `lambda.architecture` inside the container pool's run path
    // (`container-pool.ts` `startOne`), so it is NOT set on `platform` here —
    // unlike the IMAGE branch below, the ZIP `ContainerSpec` carries the
    // resolved `lambda` (with `architecture`) rather than a pre-computed
    // platform string.

    // PR 6 (#232): pre-resolve the `/opt` bind-mount source. Single-
    // layer functions reuse the layer's asset dir directly; multi-
    // layer functions get a freshly-merged tmpdir (later layers
    // overwrite earlier files via `cpSync({force:true})` — the
    // load-bearing half of AWS's "last layer wins" semantic).
    //
    // Issue #448: literal-ARN entries are downloaded + unzipped via
    // `lambda:GetLayerVersion` before the cpSync-merge step. The per-ARN
    // tmpdirs are tracked in `layerTmpDirs` alongside multi-layer merge
    // dirs so the same shutdown path cleans every one.
    optDir = await materializeLambdaLayers(lambda.layers, layerTmpDirs, layerRoleArn);
  } else {
    // IMAGE branch (closes #453): build locally from cdk.out asset
    // manifest, OR pull from ECR when no matching asset is found.
    // Issue #249 / C7 + C8 — `--no-build` and `--ecr-role-arn` are
    // threaded through `resolveContainerImageForStartApi` so the
    // local-asset build path can short-circuit and the ECR-pull
    // fallback can assume a cross-account role.
    const built = await resolveContainerImageForStartApi(lambda, skipPull, args.profile, {
      ...(args.skipBuild !== undefined && { noBuild: args.skipBuild }),
      ...(args.ecrRoleArn !== undefined && { ecrRoleArn: args.ecrRoleArn }),
      ...(args.stsRegion !== undefined && { region: args.stsRegion }),
    });
    imageRef = built.imageRef;
    platform = architectureToPlatform(lambda.architecture);
  }

  // Env vars: literal template values + --env-vars overlay. When
  // `--from-state` was passed (and state for this Lambda's stack
  // loaded), intrinsic-valued template entries are first substituted
  // against deployed-stack state + AWS pseudo parameters via
  // `substituteEnvVarsFromState`. Per-key failures (state missing for
  // a referenced logical id, attribute not captured at deploy time,
  // unsupported intrinsic shape) warn-and-drop with context. Keys the
  // state-resolver already warned about are suppressed in the downstream
  // env-resolver's generic warn loop so the user sees one warn per key.
  let templateEnv = getTemplateEnv(lambda.resource);
  const stateBundle = stateByStack.get(lambda.stack.stackName);
  let stateAudit: ReturnType<typeof substituteEnvVarsFromState>['audit'] | undefined;
  if (stateBundle) {
    const context: SubstitutionContext = { resources: stateBundle.state.resources };
    if (stateBundle.pseudoParameters) {
      context.pseudoParameters = stateBundle.pseudoParameters;
    }
    if (stateBundle.ssmParameters) {
      context.parameters = stateBundle.ssmParameters;
    }
    // Flag decrypted SecureString parameters so the consuming env keys are
    // kept off the `docker run` argv (issue #99).
    if (stateBundle.ssmSecureStringLogicalIds?.length) {
      context.sensitiveParameters = new Set(stateBundle.ssmSecureStringLogicalIds);
    }
    const { env, audit } = substituteEnvVarsFromState(templateEnv, context);
    templateEnv = env;
    for (const key of audit.resolvedKeys) {
      getLogger().debug(`Lambda ${logicalId}: state source substituted env var ${key}`);
    }
    // Deployed-env fallback: fill keys static substitution dropped (e.g.
    // `Fn::GetAtt <Sibling>.Arn`) from the consumer function's
    // deploy-time-resolved env, fetched during the state-load pass (see
    // `loadStateForRoutedStacks`). Only populated under `--from-cfn-stack`.
    let unresolved = audit.unresolved;
    const resolvedKeys = [...audit.resolvedKeys];
    const deployedEnv = stateBundle.deployedEnvByLambda?.get(logicalId);
    if (unresolved.length > 0 && deployedEnv) {
      const fb = applyDeployedEnvFallback(templateEnv, unresolved, deployedEnv);
      templateEnv = fb.env;
      unresolved = fb.stillUnresolved;
      for (const key of fb.filled) {
        resolvedKeys.push(key);
        getLogger().debug(
          `Lambda ${logicalId}: filled env var ${key} from deployed function config`
        );
      }
    }
    stateAudit = { resolvedKeys, unresolved, sensitiveKeys: audit.sensitiveKeys };
    for (const { key, reason } of unresolved) {
      getLogger().warn(
        `Lambda ${logicalId}: state source could not substitute env var ${key} (${reason}). ` +
          `Override it via --env-vars or it will be dropped.`
      );
    }
  }
  const lambdaCdkPath = readCdkPathOrUndefined(lambda.resource);
  const envResult = resolveEnvVars(logicalId, lambdaCdkPath, templateEnv, overrides);
  for (const key of envResult.unresolved) {
    // The state-resolver already warned for keys it tried + failed on
    // (defensive: substituteEnvVarsFromState drops unresolved keys from
    // the returned env so the env-resolver never sees them, but mirror
    // `cdkl invoke --from-state`'s safety dedupe in case the
    // state-resolver evolves).
    if (stateAudit && stateAudit.unresolved.some((u) => u.key === key)) continue;
    // Prefer the L2 form (`MyStack/MyFn`) in the suggestion since that
    // matches the README guidance and `cdkl invoke` target shape; the
    // resolver's prefix rule accepts either form.
    const overrideKeyExample = lambdaCdkPath?.replace(/\/Resource$/, '') ?? logicalId;
    getLogger().warn(
      `Lambda ${logicalId}: env var ${key} contains a CloudFormation intrinsic and was dropped. ` +
        `Override it with --env-vars (e.g. {"${overrideKeyExample}":{"${key}":"<literal>"}}) ` +
        `or pass a state-source flag (e.g. --from-cfn-stack or a host-provided extension) to recover deployed values.`
    );
  }

  const dockerEnv: Record<string, string> = {
    AWS_LAMBDA_FUNCTION_NAME: logicalId,
    AWS_LAMBDA_FUNCTION_MEMORY_SIZE: String(lambda.memoryMb),
    AWS_LAMBDA_FUNCTION_TIMEOUT: String(lambda.timeoutSec),
    AWS_LAMBDA_FUNCTION_VERSION: '$LATEST',
    AWS_LAMBDA_LOG_GROUP_NAME: `/aws/lambda/${logicalId}`,
    AWS_LAMBDA_LOG_STREAM_NAME: 'local',
    ...envResult.resolved,
  };

  // Issue #256 Option 1: `resolveStartApiAssumeRoleArn` honors the
  // four `--assume-role` forms: per-Lambda override > global default >
  // bare-auto-resolve (per-Lambda Role property -> ARN) > absent.
  const roleArn = resolveStartApiAssumeRoleArn({
    logicalId,
    assumeRole,
    lambdaResource: lambda.resource,
    stateBundle,
  });
  if (roleArn) {
    const creds = await assumeLambdaExecutionRole(roleArn, stsRegion, profile);
    dockerEnv['AWS_ACCESS_KEY_ID'] = creds.accessKeyId;
    dockerEnv['AWS_SECRET_ACCESS_KEY'] = creds.secretAccessKey;
    dockerEnv['AWS_SESSION_TOKEN'] = creds.sessionToken;
    if (stsRegion) dockerEnv['AWS_REGION'] = stsRegion;
  } else {
    forwardAwsEnv(dockerEnv);
    // Issue #654: when `--profile <p>` is set AND this Lambda has no
    // `--assume-role` mapping, overlay the profile-resolved credentials
    // on top of whatever `forwardAwsEnv` copied from `process.env`. The
    // overlay covers SSO / IAM Identity Center / regular access key /
    // role-assumption profiles uniformly (resolved via the SDK's
    // default credential chain). Without this overlay, a dev who runs
    // `cdkl start-api --profile dev` AND has no
    // `AWS_ACCESS_KEY_ID` env var (the common SSO / Identity Center
    // case) sees the Lambda boot with no creds → handler's AWS SDK
    // call fails with `Could not load credentials from any providers`.
    //
    // Region from forwardAwsEnv is preserved — only the credential
    // triple is overlaid. Existing precedence: assume-role > profile >
    // forwarded process env.
    if (profileCredentials) {
      dockerEnv['AWS_ACCESS_KEY_ID'] = profileCredentials.accessKeyId;
      dockerEnv['AWS_SECRET_ACCESS_KEY'] = profileCredentials.secretAccessKey;
      if (profileCredentials.sessionToken) {
        dockerEnv['AWS_SESSION_TOKEN'] = profileCredentials.sessionToken;
      } else {
        // The profile resolved to long-lived creds (no session token).
        // Strip any inherited session token to avoid the SDK trying
        // to use a mismatched (long-lived AKID + foreign session).
        delete dockerEnv['AWS_SESSION_TOKEN'];
      }
    }

    // Profile-aware SDK config inside container: when the server boot
    // synthesized a credentials file via
    // `writeProfileCredentialsFile(options.profile, ...)`, point the
    // container's SDK chain at the bind-mounted path so
    // `fromIni({ profile: '<name>' })` calls inside the handler resolve
    // to the same creds as the default chain. `AWS_PROFILE` makes
    // `fromIni()` (no explicit arg) ALSO use this profile — both code
    // paths converge on the same `[<name>]` section.
    //
    // GATED on `!roleArn` (nested in the assume-role-NOT-effective branch)
    // so the existing precedence "assume-role > profile > env" holds for
    // the file path too. Without this nesting, a handler in an
    // assume-role'd Lambda that called `fromIni({ profile: '<name>' })`
    // explicitly would silently bypass the execution-role creds and use
    // the `--profile <name>` creds — breaking the documented precedence.
    if (profileCredsFile) {
      dockerEnv['AWS_SHARED_CREDENTIALS_FILE'] = profileCredsFile.containerPath;
      dockerEnv['AWS_PROFILE'] = profileCredsFile.profileName;
    }
  }

  // Fallback AWS_REGION for the container's AWS SDK. The branches above
  // only set AWS_REGION from the assume-role STS region or a forwarded
  // AWS_REGION / AWS_DEFAULT_REGION env var. `--profile` injects the
  // credential triple but NOT the profile's region (the synthesized
  // credentials file carries no `region =`), and the synth-derived stack
  // region was previously used only host-side for the --from-cfn-stack CFn
  // client. Seed both here (precedence in resolveContainerFallbackRegion)
  // so a handler's ambient-region SDK call resolves to the same region the
  // deployed function runs in instead of failing with "Region is missing".
  if (!dockerEnv['AWS_REGION']) {
    const fallbackRegion = resolveContainerFallbackRegion({
      stackRegionOverride,
      synthRegion: lambda.stack.region,
      profileRegion,
    });
    if (fallbackRegion) dockerEnv['AWS_REGION'] = fallbackRegion;
  }

  if (debugPort !== undefined) {
    dockerEnv['NODE_OPTIONS'] = `--inspect-brk=0.0.0.0:${debugPort}`;
  }

  // Issue #440 — Lambda EphemeralStorage.Size: when the function's
  // template declared `EphemeralStorage`, plumb the configured `/tmp`
  // cap through to every cold-start of this Lambda's warm pool so
  // the local container's `/tmp` matches the deployed function's
  // sized tmpfs. Resolved here (once at server boot) rather than at
  // acquire-time because the value is template-static for the
  // server's lifetime. Applies to BOTH ZIP and IMAGE Lambdas.
  const tmpfs =
    lambda.ephemeralStorageMb !== undefined
      ? { target: '/tmp', sizeMb: lambda.ephemeralStorageMb }
      : undefined;

  // Env keys whose value resolved to a decrypted SecureString SSM parameter
  // (issue #99) — routed off the `docker run` argv via the pool's
  // value-from-process-env form.
  const sensitiveEnvKeys =
    stateAudit && stateAudit.sensitiveKeys.length > 0
      ? new Set(stateAudit.sensitiveKeys)
      : undefined;

  if (lambda.kind === 'zip') {
    const spec: ContainerSpec = {
      kind: 'zip',
      lambda,
      codeDir: codeDir!,
      env: dockerEnv,
      ...(sensitiveEnvKeys && { sensitiveEnvKeys }),
      containerHost,
      ...(optDir !== undefined && { optDir }),
      ...(debugPort !== undefined && { debugPort }),
      ...(tmpfs !== undefined && { tmpfs }),
      ...(profileCredsFile && {
        profileCredentialsFile: {
          hostPath: profileCredsFile.hostPath,
          containerPath: profileCredsFile.containerPath,
        },
      }),
    };
    return spec;
  }

  const spec: ContainerSpec = {
    kind: 'image',
    lambda,
    image: imageRef!,
    platform: platform!,
    command: lambda.imageConfig.command ?? [],
    ...(lambda.imageConfig.entryPoint !== undefined &&
      lambda.imageConfig.entryPoint.length > 0 && {
        entryPoint: lambda.imageConfig.entryPoint,
      }),
    ...(lambda.imageConfig.workingDirectory !== undefined && {
      workingDir: lambda.imageConfig.workingDirectory,
    }),
    env: dockerEnv,
    ...(sensitiveEnvKeys && { sensitiveEnvKeys }),
    containerHost,
    ...(debugPort !== undefined && { debugPort }),
    ...(tmpfs !== undefined && { tmpfs }),
    ...(profileCredsFile && {
      profileCredentialsFile: {
        hostPath: profileCredsFile.hostPath,
        containerPath: profileCredsFile.containerPath,
      },
    }),
  };
  return spec;
}

/**
 * Resolve a container Lambda's local docker image — local build from
 * `cdk.out` asset manifest first, ECR-pull fallback when the asset
 * manifest has no matching entry. Mirrors `cdkl invoke`'s
 * `resolveContainerImagePlan` shape.
 *
 * Issue #249 / C7 + C8 — `noBuild` and `ecrRoleArn` are accepted for
 * parity with `cdkl invoke` / `cdkl run-task`. `noBuild` short-circuits
 * the local-asset build path (the deterministic content-addressed tag
 * must already be in the local registry). `ecrRoleArn` lets the
 * ECR-pull fallback authenticate against cross-account / centralized
 * registries via STS AssumeRole — same-account / same-region pulls do
 * not need it.
 */
export async function resolveContainerImageForStartApi(
  lambda: ResolvedStartApiImageLambda,
  skipPull: boolean,
  profile?: string,
  options?: { noBuild?: boolean; ecrRoleArn?: string; region?: string }
): Promise<{ imageRef: string }> {
  const logger = getLogger();
  const localBuild = await resolveLocalBuildPlan(lambda);
  if (localBuild) {
    const imageRef = await buildContainerImage(localBuild.asset, localBuild.cdkOutDir, {
      architecture: lambda.architecture,
      ...(options?.noBuild !== undefined && { noBuild: options.noBuild }),
    });
    return { imageRef };
  }
  if (!parseEcrUri(lambda.imageUri)) {
    throw new Error(
      `Container Lambda '${lambda.logicalId}' has no matching asset in cdk.out, and Code.ImageUri ` +
        `'${lambda.imageUri}' is not an ECR URI ${getEmbedConfig().binaryName} can authenticate against. ` +
        'Re-synthesize the CDK app (so cdk.out includes the build context) or deploy the image to ECR first.'
    );
  }
  logger.info(`No matching cdk.out asset for ${lambda.imageUri}; falling back to ECR pull...`);
  const imageRef = await pullEcrImage(lambda.imageUri, {
    skipPull,
    ...(profile !== undefined && { profile }),
    ...(options?.ecrRoleArn !== undefined && { ecrRoleArn: options.ecrRoleArn }),
    ...(options?.region !== undefined && { region: options.region }),
  });
  return { imageRef };
}

/**
 * Look up the docker image asset that backs a container Lambda.
 * Returns `undefined` when the asset manifest has no matching entry —
 * the caller falls back to the ECR-pull path.
 *
 * Mirrors `local-invoke.ts:resolveLocalBuildPlan`; kept separate so
 * the two commands evolve their asset-lookup heuristics independently.
 */
async function resolveLocalBuildPlan(
  lambda: ResolvedStartApiImageLambda
): Promise<{ asset: { source: DockerImageAssetSource }; cdkOutDir: string } | undefined> {
  const manifestPath = lambda.stack.assetManifestPath;
  if (!manifestPath) return undefined;
  const cdkOutDir = path.dirname(manifestPath);
  const loader = new AssetManifestLoader();
  const manifest = await loader.loadManifest(cdkOutDir, lambda.stack.stackName);
  if (!manifest) return undefined;
  const entry = getDockerImageBySourceHash(manifest, lambda.imageUri);
  if (!entry) return undefined;
  return { asset: entry.asset, cdkOutDir };
}

/**
 * Build the `/opt` bind-mount source for a Lambda's layers. Mirrors
 * the helper in `src/cli/commands/local-invoke.ts` but stores the
 * merged tmpdir into the shared `layerTmpDirs` set so the server's
 * graceful shutdown path can clean it up. Returns `undefined` when
 * the function declares no layers.
 *
 * Three branches:
 *   - 0 layers → `undefined` (no `/opt` mount).
 *   - 1 layer → bind-mount the layer's asset dir directly (no copy)
 *     when the entry is a same-stack asset. Literal-ARN entries always
 *     pre-materialize first.
 *   - 2+ layers → copy each into a fresh tmpdir IN ORDER (later
 *     layers overwrite earlier files via `cpSync({force: true})`),
 *     bind-mount the tmpdir at `/opt`. Records the tmpdir in
 *     `layerTmpDirs` so `shutdown(...)` removes it.
 *
 * Issue #448: literal-ARN entries (`{kind: 'arn', ...}`) are downloaded
 * + unzipped via `lambda:GetLayerVersion` BEFORE the cpSync-merge
 * branches run. Every per-ARN tmpdir is also recorded in `layerTmpDirs`
 * so the same shutdown path cleans it up — even for the single-layer
 * fast path that bind-mounts the dir directly.
 *
 * AWS Lambda's actual runtime extracts every layer ZIP into `/opt`
 * in template order — the merge mirrors that. Docker rejects multiple
 * `-v ...:/opt:ro` entries at the same target, so cdk-local can't rely on
 * overlay layering and must produce a single merged dir on the host.
 */
export async function materializeLambdaLayers(
  layers: ResolvedLambdaLayer[],
  layerTmpDirs: Set<string>,
  layerRoleArn: string | undefined
): Promise<string | undefined> {
  if (layers.length === 0) return undefined;

  // Stage 1: pre-materialize every literal-ARN entry into its own
  // tmpdir (issue #448). The resulting flat list of `assetPath`
  // entries is what the existing single-layer / multi-layer merge
  // branches consume.
  const flat: { logicalId: string; assetPath: string }[] = [];
  for (const layer of layers) {
    if (layer.kind === 'asset') {
      flat.push({ logicalId: layer.logicalId, assetPath: layer.assetPath });
      continue;
    }
    const dir = await materializeLayerFromArn(layer, {
      ...(layerRoleArn !== undefined && { roleArn: layerRoleArn }),
    });
    layerTmpDirs.add(dir);
    flat.push({ logicalId: layer.arn, assetPath: dir });
  }

  if (flat.length === 1) return flat[0]!.assetPath;
  const dir = mkdtempSync(
    path.join(tmpdir(), `${getEmbedConfig().resourceNamePrefix}-start-api-layers-`)
  );
  for (const layer of flat) {
    // `recursive: true` enables the directory copy. `force: true`
    // implements AWS's "last layer wins" file-collision semantic: a
    // later layer's entry at the same relative path overwrites the
    // earlier one.
    //
    // **Contract pinned (Node 20+)**: this call relies on `fs.cpSync`
    // defaults that the integ-test fixture (`tests/integration/local-
    // invoke-layers/`) exercises end-to-end, and that future
    // refactors must NOT silently drop:
    //   - `mode` defaults to preserving the source's file-mode bits,
    //     including `+x`. AWS layers commonly ship executable scripts
    //     under `bin/` and a handler that runs `/opt/bin/<script>`
    //     would otherwise fail with "Permission denied".
    //   - `verbatimSymlinks` defaults to true on Node 20+; symlinks
    //     are copied as symlinks (not dereferenced), matching AWS's
    //     layer-ZIP extraction into `/opt`.
    // Mirrors the same contract pinned in `local-invoke.ts`'s
    // `materializeLambdaLayers`; keep the two call sites in sync if
    // they ever consolidate into one helper.
    cpSync(layer.assetPath, dir, { recursive: true, force: true });
  }
  layerTmpDirs.add(dir);
  return dir;
}

/**
 * Locate a Lambda by logical ID across the target stacks. Throws when
 * no stack contains a matching `AWS::Lambda::Function` — at this point
 * route discovery has already linked the routes to logical IDs, so a
 * miss here is a synthesis bug worth surfacing.
 */
/**
 * Discriminated union covering both ZIP and container Lambdas (closes
 * #453). The ZIP variant is the original `cdkl start-api` v1
 * shape; the IMAGE variant was unlocked here in this PR — the per-
 * Lambda image build / ECR pull / `--platform` threading lives below
 * in `buildContainerSpec`'s `kind === 'image'` branch.
 */
type ResolvedStartApiLambda = ResolvedStartApiZipLambda | ResolvedStartApiImageLambda;

interface ResolvedStartApiLambdaBase {
  stack: StackInfo;
  logicalId: string;
  resource: TemplateResource;
  memoryMb: number;
  timeoutSec: number;
  /**
   * Resolved same-stack `Properties.Layers` references. Populated on
   * the ZIP branch; always `[]` on the IMAGE branch — container
   * Lambdas reject `Layers` at deploy time on the AWS side (layers
   * are baked into the image at build time, not overlaid at
   * runtime), so cdk-local silently ignores any `Layers` property to
   * match AWS's invoke-time behavior. The base-shape `[]` keeps the
   * ResolvedImageLambda → `lambda-resolver.ResolvedImageLambda`
   * cast structurally valid in the container-pool spec.
   */
  layers: ResolvedLambdaLayer[];
  /**
   * `Properties.EphemeralStorage.Size` (issue #440), MiB. Parsed via
   * the shared `extractEphemeralStorageMb` helper so the CFn-range
   * validation (reject > 10240) matches `cdkl invoke`. Plumbed
   * into the warm container's `--tmpfs /tmp:size=Nm` so handlers in
   * the start-api server see the same sized `/tmp` cap they would on
   * the deployed function. Applies to BOTH ZIP and IMAGE Lambdas —
   * Docker `--tmpfs` overlays inside any container image just like on
   * the public base images. Undefined when the property is absent.
   */
  ephemeralStorageMb?: number;
}

interface ResolvedStartApiZipLambda extends ResolvedStartApiLambdaBase {
  kind: 'zip';
  runtime: string;
  handler: string;
  codePath: string | null;
  inlineCode?: string;
  /**
   * `Architectures: [x86_64]` (default) or `[arm64]` — pins the warm
   * container's `--platform` so a `provided.*` `bootstrap` compiled for the
   * other arch does not fail with `exec format error`.
   */
  architecture: 'x86_64' | 'arm64';
}

export interface ResolvedStartApiImageLambda extends ResolvedStartApiLambdaBase {
  kind: 'image';
  /**
   * Raw `Code.ImageUri` value, surfaced for the local-build path's
   * asset-hash extraction AND for the ECR-pull fallback. Already
   * resolved through cdk-assets bootstrap-placeholder substitution
   * upstream — `${AWS::*}` pseudo-parameters are still present and
   * substituted at the lookup site.
   */
  imageUri: string;
  /**
   * `ImageConfig` (all fields optional). Most container Lambdas leave
   * `EntryPoint` unset so `/lambda-entrypoint.sh` stays in charge of
   * RIE dispatch. `Command` is typically the handler reference, e.g.
   * `['app.handler']`.
   */
  imageConfig: {
    command?: string[];
    entryPoint?: string[];
    workingDirectory?: string;
  };
  /**
   * `Architectures: [x86_64]` (default) or `[arm64]`. Threaded through
   * to `--platform linux/amd64` / `linux/arm64` on BOTH `docker build`
   * AND `docker run` so an arm64 host running an x86_64 Lambda doesn't
   * hit silent emulation, and an x86_64 host running an arm64 Lambda
   * doesn't fail with `exec format error`.
   */
  architecture: 'x86_64' | 'arm64';
}

export function resolveLambdaByLogicalId(
  logicalId: string,
  stacks: StackInfo[]
): ResolvedStartApiLambda {
  for (const stack of stacks) {
    const resource = stack.template.Resources?.[logicalId];
    if (!resource || resource.Type !== 'AWS::Lambda::Function') continue;
    const props = resource.Properties ?? {};
    const memoryMb = typeof props['MemorySize'] === 'number' ? props['MemorySize'] : 128;
    const timeoutSec = typeof props['Timeout'] === 'number' ? props['Timeout'] : 3;

    const code = (props['Code'] ?? {}) as Record<string, unknown>;
    const imageUri = extractImageUri(
      code['ImageUri'],
      logicalId,
      stack.stackName,
      stack.template.Resources ?? {},
      stack.region
    );
    if (imageUri !== undefined) {
      return resolveImageLambda({
        stack,
        logicalId,
        resource,
        props,
        memoryMb,
        timeoutSec,
        imageUri,
      });
    }

    // ZIP branch: Runtime + Handler mandatory.
    const runtime = typeof props['Runtime'] === 'string' ? props['Runtime'] : '';
    const handler = typeof props['Handler'] === 'string' ? props['Handler'] : '';
    if (!runtime) {
      throw new Error(
        `Lambda '${logicalId}' has no Runtime property and no Code.ImageUri. ` +
          `${getEmbedConfig().cliName} start-api cannot tell if this is a ZIP or a container Lambda.`
      );
    }
    if (!handler) {
      throw new Error(`Lambda '${logicalId}' has no Handler property.`);
    }
    const inlineCode = typeof code['ZipFile'] === 'string' ? code['ZipFile'] : undefined;
    let codePath: string | null = null;
    if (!inlineCode) {
      codePath = resolveAssetCodePath(stack, logicalId, resource);
    }
    // PR 6 (#232): same-stack `Properties.Layers` references resolve to
    // local asset directories that bind-mount at `/opt`; start-api
    // routes through the same lambda-resolver helper as `cdk-local local
    // invoke` so the warm container pool gets layer support out of
    // the box.
    const layers = resolveLambdaLayers(stack, logicalId, props);
    const ephemeralStorageMb = extractEphemeralStorageMb(props, logicalId);
    // Pin the warm container's platform to the declared architecture (default
    // x86_64) so a `provided.*` `bootstrap` does not hit an "exec format error".
    // Shared with `cdkl invoke`'s resolver so an unsupported value errors
    // consistently rather than being silently coerced to x86_64.
    const architecture = resolveLambdaArchitecture(props, logicalId);
    return {
      kind: 'zip',
      stack,
      logicalId,
      resource,
      runtime,
      handler,
      memoryMb,
      timeoutSec,
      codePath,
      layers,
      architecture,
      ...(inlineCode !== undefined && { inlineCode }),
      ...(ephemeralStorageMb !== undefined && { ephemeralStorageMb }),
    };
  }
  throw new Error(
    `No AWS::Lambda::Function resource named '${logicalId}' found in target stacks. This is likely a synthesis bug — the route-discovery phase resolved a route to this logical ID.`
  );
}

/**
 * Extract `Code.ImageUri` across the shapes CDK actually synthesizes.
 * Mirrors the simpler subset of `lambda-resolver.ts:extractImageUri`
 * scoped to the shapes `cdkl start-api` consumes — flat string,
 * `Fn::Sub` (the canonical asset shape for
 * `lambda.DockerImageCode.fromImageAsset`), and `Fn::Join` (the
 * canonical shape for `lambda.DockerImageCode.fromImageAsset` in
 * CDK 2.x, which emits a `Fn::Join` over the literal bootstrap ECR
 * URI with `${AWS::URLSuffix}` — issues #627 + #637).
 *
 * The `Fn::Join` arm routes through the shared
 * `tryResolveImageFnJoin` helper (`src/local/intrinsic-image.ts`) used
 * by `cdkl invoke`. When the synth template recorded a deploy
 * region (`stack.region`), we derive `{ urlSuffix, partition, region }`
 * via `derivePseudoParametersFromRegion` and pass it as the resolver's
 * `pseudoParameters` block so the canonical `${AWS::URLSuffix}` shape
 * resolves cleanly (issue #637). Same-stack ECR refs still surface as
 * `needs-state` (those require `--from-state`); a Join that references
 * `${AWS::AccountId}` without state still surfaces as `not-applicable`
 * with a more specific error message naming the missing parameter.
 *
 * Returns `undefined` when the field is absent or non-recognized,
 * which routes the caller to the ZIP branch (with its existing
 * "no Runtime / no Handler" validations).
 */
function extractImageUri(
  value: unknown,
  logicalId: string,
  stackName: string,
  resources: Record<string, TemplateResource>,
  region: string | undefined
): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const sub = obj['Fn::Sub'];
    if (typeof sub === 'string' && sub.length > 0) return sub;
    if (Array.isArray(sub) && typeof sub[0] === 'string') return sub[0];

    if ('Fn::Join' in obj) {
      // Issue #637: derive the region-knowable pseudo parameters
      // (`urlSuffix` / `partition` / `region`) and pass them as
      // resolver context so the canonical `lambda.DockerImageCode
      // .fromImageAsset` shape resolves without `--from-state`.
      // `accountId` stays undefined — the canonical shape has it
      // as a literal in the template, and a Join that genuinely
      // references `${AWS::AccountId}` falls back to the
      // `not-applicable` branch below with a clear error.
      const pseudoParameters = derivePseudoParametersFromRegion(region);
      const joinResolved = tryResolveImageFnJoin(
        value,
        resources,
        pseudoParameters ? { pseudoParameters } : undefined
      );
      if (joinResolved.kind === 'resolved') return joinResolved.uri;
      if (joinResolved.kind === 'needs-state') {
        throw new Error(
          `Lambda '${logicalId}' in ${stackName} references same-stack ECR repository '${joinResolved.repoLogicalId}' via Fn::Join. ` +
            `${getEmbedConfig().cliName} cannot resolve the repository URI without state — ` +
            'deploy the stack first, rebuild via lambda.DockerImageCode.fromImageAsset, or pin a public image.'
        );
      }
      if (joinResolved.kind === 'unsupported-join') {
        throw new Error(
          `Lambda '${logicalId}' in ${stackName} has an unsupported Fn::Join Code.ImageUri shape: ${joinResolved.reason}. ` +
            `${getEmbedConfig().cliName} recognizes the canonical CDK 2.x lambda.DockerImageCode.fromEcr Fn::Join shape ` +
            '(delimiter "" with nested Fn::Select/Fn::Split over an ECR Repository Arn GetAtt + Ref to the repo).'
        );
      }
      // `not-applicable` — the Join couldn't reduce every element
      // AND there's no same-stack ECR Repository ref. With #637's
      // pseudo-parameter plumbing the typical remaining cause is
      // `${AWS::AccountId}` (needs an STS call or `--from-state`)
      // or an unknown region (`stack.region` was undefined so we
      // couldn't derive `urlSuffix`). Either way, surface a clear
      // error instead of falling through to ZIP-branch validation.
      const accountIdHint = pseudoParameters
        ? ` (likely \${AWS::AccountId}, which ${getEmbedConfig().binaryName} cannot derive without a state source or STS)`
        : ` (${getEmbedConfig().binaryName} could not derive AWS pseudo parameters because stack.region was undefined)`;
      throw new Error(
        `Lambda '${logicalId}' in ${stackName} has an Fn::Join Code.ImageUri that ${getEmbedConfig().cliName} cannot resolve${accountIdHint}. ` +
          'Workarounds: deploy first and run with a state-source flag (e.g. --from-cfn-stack or a host-provided extension), or pin a fully-literal public image URI.'
      );
    }
  }
  return undefined;
}

/**
 * Build the IMAGE-variant `ResolvedStartApiLambda` from a Lambda
 * template entry with `Code.ImageUri`. Mirrors
 * `lambda-resolver.ts:extractImageLambdaProperties` but trimmed to the
 * fields `cdkl start-api` actually consumes.
 */
function resolveImageLambda(args: {
  stack: StackInfo;
  logicalId: string;
  resource: TemplateResource;
  props: Record<string, unknown>;
  memoryMb: number;
  timeoutSec: number;
  imageUri: string;
}): ResolvedStartApiImageLambda {
  const { stack, logicalId, resource, props, memoryMb, timeoutSec, imageUri } = args;

  const rawImageConfig = (props['ImageConfig'] ?? {}) as Record<string, unknown>;
  const imageConfig: ResolvedStartApiImageLambda['imageConfig'] = {};
  if (Array.isArray(rawImageConfig['Command'])) {
    imageConfig.command = rawImageConfig['Command'].filter(
      (s): s is string => typeof s === 'string'
    );
  }
  if (Array.isArray(rawImageConfig['EntryPoint'])) {
    imageConfig.entryPoint = rawImageConfig['EntryPoint'].filter(
      (s): s is string => typeof s === 'string'
    );
  }
  if (typeof rawImageConfig['WorkingDirectory'] === 'string') {
    imageConfig.workingDirectory = rawImageConfig['WorkingDirectory'];
  }

  // Architectures defaults to x86_64. CDK only ever sets one entry.
  const arches = props['Architectures'];
  let architecture: 'x86_64' | 'arm64' = 'x86_64';
  if (Array.isArray(arches) && arches.length > 0) {
    const first: unknown = arches[0];
    if (first === 'arm64') architecture = 'arm64';
    else if (first === 'x86_64') architecture = 'x86_64';
    else {
      throw new Error(
        `Lambda '${logicalId}' has unsupported Architectures value '${String(first)}'. ` +
          `${getEmbedConfig().cliName} start-api supports x86_64 and arm64.`
      );
    }
  }

  // Issue #440 — EphemeralStorage.Size applies to container Lambdas
  // too; AWS accepts it on `lambda.DockerImageFunction`. Parse via the
  // shared helper for CFn-range validation parity with the ZIP branch.
  const ephemeralStorageMb = extractEphemeralStorageMb(props, logicalId);

  return {
    kind: 'image',
    stack,
    logicalId,
    resource,
    memoryMb,
    timeoutSec,
    imageUri,
    imageConfig,
    architecture,
    layers: [],
    ...(ephemeralStorageMb !== undefined && { ephemeralStorageMb }),
  };
}

/**
 * Locate the Lambda's local code directory using the CDK-blessed
 * `Metadata['aws:asset:path']` hint. Bind-mounted directly at
 * `/var/task` (read-only) by the docker-runner.
 */
function resolveAssetCodePath(
  stack: StackInfo,
  logicalId: string,
  resource: TemplateResource
): string {
  const meta = resource.Metadata;
  const assetPath = meta?.['aws:asset:path'];
  if (typeof assetPath !== 'string' || assetPath.length === 0) {
    throw new Error(
      `Lambda '${logicalId}' has no Metadata['aws:asset:path']. ${getEmbedConfig().cliName} start-api needs this hint to find the local asset directory. Re-synthesize the app and retry.`
    );
  }
  const cdkOutDir = stack.assetManifestPath ? path.dirname(stack.assetManifestPath) : process.cwd();
  return path.isAbsolute(assetPath) ? assetPath : path.resolve(cdkOutDir, assetPath);
}

/**
 * Print the discovered route table to stdout. Format mirrors the spec
 * doc's example so verify.sh / users can read it at a glance.
 *
 * Routes with `unsupported` or `mockCors` are annotated so the user can
 * tell at a glance which routes will dispatch to a Lambda vs which
 * return 501 / 204 directly:
 *   - normal:        `GET /items -> Handler  (HTTP API)`
 *   - mockCors:      `OPTIONS /items -> [MOCK CORS preflight]  (REST v1, stage 'prod')`
 *   - unsupported:   `POST /admin -> [501 Not Implemented]  (HTTP API)`
 */
function printRouteTable(routes: readonly RouteWithAuth[]): void {
  const flat = routes.map((r) => r.route);
  const sorted = [...flat].sort((a, b) => {
    if (a.pathPattern !== b.pathPattern) return a.pathPattern.localeCompare(b.pathPattern);
    return a.method.localeCompare(b.method);
  });
  const methodWidth = Math.max(...sorted.map((r) => r.method.length), 6);
  const pathWidth = Math.max(...sorted.map((r) => r.pathPattern.length), 8);
  process.stdout.write('Discovered routes:\n');
  for (const r of sorted) {
    const sourceLabel =
      r.source === 'http-api'
        ? 'HTTP API'
        : r.source === 'rest-v1'
          ? `REST v1, stage '${r.stage}'`
          : 'Function URL';
    const target = r.mockCors
      ? '[MOCK CORS preflight]'
      : r.unsupported
        ? '[501 Not Implemented]'
        : r.serviceIntegration
          ? `[${r.serviceIntegration.subtype}]`
          : r.restV1Integration
            ? formatRestV1IntegrationLabel(r.restV1Integration)
            : r.lambdaLogicalId;
    process.stdout.write(
      `  ${r.method.padEnd(methodWidth)}  ${r.pathPattern.padEnd(pathWidth)}  -> ${target}  (${sourceLabel})\n`
    );
  }
  process.stdout.write('\n');
}

/**
 * Format the route-table label for a REST v1 non-AWS_PROXY integration.
 * `MOCK` / `HTTP` / `HTTP_PROXY` show their integration kind directly;
 * `AWS` (Lambda non-proxy) shows the Lambda logical id with an `[AWS]`
 * suffix so it's distinguishable from AWS_PROXY rows. Closes #457.
 */
function formatRestV1IntegrationLabel(
  integration: NonNullable<DiscoveredRoute['restV1Integration']>
): string {
  switch (integration.kind) {
    case 'mock':
      return '[MOCK]';
    case 'http-proxy':
      return `[HTTP_PROXY ${integration.uri}]`;
    case 'http':
      return `[HTTP ${integration.uri}]`;
    case 'aws-lambda':
      return `${integration.lambdaLogicalId} [AWS]`;
  }
}

/**
 * Materialize an inline Lambda body (`Code.ZipFile`) to a tmpdir and
 * return the directory the container should mount at /var/task.
 * Mirrors `cdkl invoke`'s implementation; the only divergence is
 * the long-running-server lifecycle: every tmpdir created here is
 * recorded in `tmpDirsOut` so the caller's shutdown path can `rmSync`
 * them. (`cdkl invoke` runs once and `--rm` is the right model;
 * `cdkl start-api` lives across requests, so leaks compound.)
 */
function materializeInlineCode(
  handler: string,
  source: string,
  fileExtension: string,
  tmpDirsOut: Set<string>
): string {
  const lastDot = handler.lastIndexOf('.');
  if (lastDot <= 0) {
    throw new Error(`Handler '${handler}' is malformed: expected '<modulePath>.<exportName>'.`);
  }
  const modulePath = handler.substring(0, lastDot);
  const dir = mkdtempSync(path.join(tmpdir(), `${getEmbedConfig().resourceNamePrefix}-start-api-`));
  tmpDirsOut.add(dir);
  const filePath = path.join(dir, `${modulePath}${fileExtension}`);
  mkdirSync(path.dirname(filePath), { recursive: true });
  writeFileSync(filePath, source, 'utf-8');
  return dir;
}

/** Pull `Properties.Environment.Variables` (when present). */
function getTemplateEnv(resource: {
  Properties?: Record<string, unknown>;
}): Record<string, unknown> | undefined {
  const props = resource.Properties ?? {};
  const env = props['Environment'];
  if (!env || typeof env !== 'object') return undefined;
  const vars = (env as Record<string, unknown>)['Variables'];
  if (!vars || typeof vars !== 'object') return undefined;
  return vars as Record<string, unknown>;
}

/** Read the SAM-shape `--env-vars` JSON file. */
function readEnvOverridesFile(filePath: string | undefined): EnvOverrideFile | undefined {
  if (!filePath) return undefined;
  let raw: string;
  try {
    raw = readFileSync(filePath, 'utf-8');
  } catch (err) {
    throw new Error(
      `Failed to read --env-vars file '${filePath}': ${err instanceof Error ? err.message : String(err)}`
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `Failed to parse --env-vars file '${filePath}' as JSON: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`--env-vars file '${filePath}' must contain a JSON object at the top level.`);
  }
  return parsed as EnvOverrideFile;
}

/**
 * Forward the developer's AWS credentials into the container so the
 * handler's AWS SDK calls can authenticate. Used when --assume-role is
 * NOT set for that Lambda — SAM-compatible default.
 */
function forwardAwsEnv(env: Record<string, string>): void {
  const passThrough = [
    'AWS_ACCESS_KEY_ID',
    'AWS_SECRET_ACCESS_KEY',
    'AWS_SESSION_TOKEN',
    'AWS_REGION',
    'AWS_DEFAULT_REGION',
  ] as const;
  for (const key of passThrough) {
    const value = process.env[key];
    if (value !== undefined) env[key] = value;
  }
}

/**
 * Re-export the shared `resolveProfileCredentials` helper that drives
 * `--profile` resolution for every `cdkl` subcommand (see
 * `src/utils/profile-resolver.ts` for the helper). Kept as a re-export
 * here so existing importers (cdkd, in-repo callers) keep working
 * without a path migration — the helper itself lives in `src/utils/`
 * so it is reachable from every command without an awkward cross-
 * command import (issue #245).
 */
export { resolveProfileCredentials };

/**
 * Resolve the fallback `AWS_REGION` for a Lambda container when neither
 * the assume-role STS region nor a forwarded `AWS_REGION` /
 * `AWS_DEFAULT_REGION` env var already set one.
 *
 * Precedence mirrors where the deployed function would actually run, so a
 * handler's ambient-region SDK call (`new XxxClient({})`) reaches the same
 * region locally as the resources it is bound to:
 *
 *   1. `--stack-region` — explicit; also drives the `--from-cfn-stack` CFn
 *      client, so the container matches the region the bound stack was read
 *      from.
 *   2. the synth-derived stack region — `env.region` on the CDK stack, read
 *      from the cloud assembly manifest (`StackInfo.region`). Previously
 *      used only host-side for the `--from-cfn-stack` CFn client; never
 *      injected into the container.
 *   3. the `--profile`'s configured region — `~/.aws/config`'s `region =`
 *      for the profile. `--profile` injected credentials but not this.
 *
 * Returns `undefined` when none is known; the caller leaves `AWS_REGION`
 * unset so the SDK's own "Region is missing" surfaces (the correct signal
 * for a region-agnostic stack with no profile region and no `AWS_REGION`
 * env).
 */
export function resolveContainerFallbackRegion(args: {
  stackRegionOverride?: string | undefined;
  synthRegion?: string | undefined;
  profileRegion?: string | undefined;
}): string | undefined {
  return args.stackRegionOverride ?? args.synthRegion ?? args.profileRegion;
}

/**
 * Issue an STS AssumeRole and return temporary credentials, via the
 * shared `assumeRoleCredentials` helper in `utils/role-arn.ts`
 * (issue #509 — previously a byte-for-byte copy of `cdkl invoke`'s
 * inline helper).
 */
async function assumeLambdaExecutionRole(
  roleArn: string,
  region: string | undefined,
  profile: string | undefined
): Promise<{ accessKeyId: string; secretAccessKey: string; sessionToken: string }> {
  return assumeRoleCredentials({ roleArn, region, profile, sessionNameSuffix: 'start-api' });
}

/**
 * Parse / clamp the `--per-lambda-concurrency` flag. Above-cap values
 * are clamped to 4 with a warn line (per the spec doc's risk-mitigation
 * row).
 */
function parsePerLambdaConcurrency(raw: string): number {
  const parsed = parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error(`--per-lambda-concurrency must be a positive integer (got '${raw}')`);
  }
  if (parsed > 4) {
    getLogger().warn(
      `--per-lambda-concurrency ${parsed} exceeds the v1 cap of 4; clamping to 4. (Raise this in a follow-up PR if your workload needs more.)`
    );
    return 4;
  }
  return parsed;
}

/**
 * One booted HTTP server tied to a single API surface (issue #260).
 * The CLI keeps an array of these to drive per-server route tables,
 * shutdown, and hot-reload state swaps.
 *
 * `group` is intentionally **mutable** — hot-reload swaps the group
 * in place after `setServerState` so post-reload route-table reprints
 * (`printPerServerRouteTables(servers)`) reflect the new routes,
 * including any new `mockCors` / `unsupported` classifications.
 * Pre-fix the field was readonly and the printed table after reload
 * always showed the boot-time routes.
 */
interface BootedApiServer {
  group: ApiServerGroup;
  readonly server: StartedApiServer;
}

/**
 * One booted WebSocket API server (#462). Mirrors `BootedApiServer`
 * for the upgrade-event-driven WebSocket family. Each instance owns:
 *
 *   - `server`: the underlying `node:http` (or `https` under mTLS)
 *     server hosting both the upgrade-listener AND the
 *     `@connections/<id>` HTTP data plane via the preDispatch hook.
 *   - `attached`: the WebSocket-server wrapper (connection registry +
 *     close handle). The HTTP server's preDispatch closure reads the
 *     registry to route management-API POST calls.
 *   - `apiPath`: the URL path the upgrade request must match
 *     (default `/<stage>` so multi-API setups stay disambiguated).
 *
 * Hot reload (`--watch`) for WebSocket APIs is restart-only in v1 —
 * see design doc §12 Q5. The CLI surfaces a warn line when a watched
 * file change implies a WebSocket route / Lambda change so the user
 * knows to restart.
 */
interface BootedWebSocketServer {
  api: DiscoveredWebSocketApi;
  readonly server: StartedApiServer;
  readonly attached: AttachedWebSocketServer;
  readonly apiPath: string;
}

/**
 * Filter the global Lambda spec map to just the Lambdas reachable from
 * one API server group. The container pool for that server is built
 * from this filtered map so per-API authorizer Lambdas + route
 * handlers stay scoped to their owning server — disposing one server's
 * pool on shutdown does NOT touch another server's still-warm
 * containers.
 *
 * Also includes any authorizer Lambdas attached to the group's routes
 * (a Lambda authorizer is a Lambda the pool needs to know about, even
 * though no route directly handles `lambdaLogicalId === auth.lambdaLogicalId`).
 */
function filterSpecsForGroup(
  group: ApiServerGroup,
  allSpecs: Map<string, ContainerSpec>
): Map<string, ContainerSpec> {
  const ids = new Set<string>();
  for (const rwa of group.routes) {
    ids.add(rwa.route.lambdaLogicalId);
    const auth = rwa.authorizer;
    if (auth && (auth.kind === 'lambda-token' || auth.kind === 'lambda-request')) {
      ids.add(auth.lambdaLogicalId);
    }
  }
  const out = new Map<string, ContainerSpec>();
  for (const id of ids) {
    const spec = allSpecs.get(id);
    if (spec) out.set(id, spec);
  }
  return out;
}

/**
 * Print one route table per server, with the server's display name as
 * the section header. Replaces the pre-issue #260 single flat table —
 * users now see exactly which routes belong to which API + port.
 */
function printPerServerRouteTables(servers: readonly BootedApiServer[]): void {
  for (const { group, server } of servers) {
    process.stdout.write(
      `\n${group.displayName}  (http://${formatAuthority(server.host, server.port)})\n`
    );
    printRouteTable(group.routes);
  }
}

/**
 * Surface every `unsupported` route (deferred 501) as a startup warn so
 * the user sees what isn't reachable BEFORE they try to curl it. One
 * warn line per route — the route's `unsupported.reason` already names
 * the offender + the underlying limitation, so we just prefix with
 * method + path. Returns the number of unsupported routes so the caller
 * can emit a single-line summary header above the list.
 */
function warnUnsupportedRoutes(
  routes: readonly DiscoveredRoute[],
  logger: ReturnType<typeof getLogger>
): number {
  const unsupported = routes.filter((r) => r.unsupported);
  if (unsupported.length === 0) return 0;
  logger.warn(
    `${unsupported.length} route(s) will respond HTTP 501 Not Implemented when hit (boot continued):`
  );
  for (const r of unsupported) {
    logger.warn(`  - ${r.method} ${r.pathPattern}: ${r.unsupported!.reason}`);
  }
  return unsupported.length;
}

/**
 * Surface every WebSocket API tagged as unsupported at discovery as a
 * startup warn. The boot loop above skips attaching the server for
 * these APIs, so no upgrade requests are ever accepted on them —
 * mirrors `warnUnsupportedRoutes`'s shape but for the WebSocket axis.
 * Typical trigger: a Route declaring `AuthorizationType !== 'NONE'` on
 * `$connect` (cdkl v1 does not emulate WebSocket authorizers; closing
 * this gap structurally rather than silently admitting
 * unauthenticated clients matches the security-by-default precedent
 * PR #514 set for HTTP API v2 service integrations).
 */
function warnUnsupportedWebSocketApis(
  apis: readonly DiscoveredWebSocketApi[],
  logger: ReturnType<typeof getLogger>
): number {
  const unsupported = apis.filter((api) => api.unsupported);
  if (unsupported.length === 0) return 0;
  logger.warn(
    `${unsupported.length} WebSocket API(s) will NOT accept upgrade requests (boot continued):`
  );
  for (const api of unsupported) {
    logger.warn(`  - ${api.declaredAt}: ${api.unsupported!.reason}`);
  }
  return unsupported.length;
}

/**
 * Surface a one-line warn per HTTP / HTTP_PROXY integration whose
 * `Integration.Uri` points at a well-known internal address space
 * (AWS IMDS, loopback, link-local, RFC1918). PR #505 / issue #457
 * follow-up: cdk-local does NOT block these — warn-and-proceed matches the
 * cognito JWKS pass-through pattern — but the user should see the
 * destination at boot so a malicious / typo'd template Uri does not
 * silently exfiltrate credentials in CI. Deduplicated per-Uri.
 */
function warnSsrfRiskyIntegrations(
  routes: readonly DiscoveredRoute[],
  logger: ReturnType<typeof getLogger>
): void {
  const seen = new Set<string>();
  for (const r of routes) {
    const integ = r.restV1Integration;
    if (!integ) continue;
    if (integ.kind !== 'http' && integ.kind !== 'http-proxy') continue;
    if (seen.has(integ.uri)) continue;
    seen.add(integ.uri);
    warnSsrfRiskyUri(integ.uri, `${r.method} ${r.pathPattern}`, (msg) => logger.warn(msg));
  }
}

/**
 * One reload cycle for the multi-server topology (issue #260). The
 * watcher serializes calls via a chain promise; this function:
 *
 *   1. Re-runs `synthesizeAndBuild()` once (failure → warn + keep
 *      previous version serving on every server).
 *   2. Re-groups the new routes by API server key.
 *   3. For each existing server, swaps state to the new group's
 *      routes + a freshly-built pool filtered to that group's
 *      Lambdas. Disposes the previous pool in the background.
 *   4. Warns about new groups (= an API was added in CDK code) and
 *      vanished groups (= an API was removed) — those require a
 *      server restart in v1.
 */
async function reloadAllServers(args: {
  synthesizeAndBuild: () => Promise<NextStateMaterial>;
  servers: readonly BootedApiServer[];
  buildPool: (specs: Map<string, ContainerSpec>) => ContainerPool;
  logger: ReturnType<typeof getLogger>;
}): Promise<void> {
  const { synthesizeAndBuild, servers, buildPool, logger } = args;
  let material: NextStateMaterial;
  try {
    material = await synthesizeAndBuild();
  } catch (err) {
    logger.warn(
      `cdk synth failed during reload; keeping previous version. (${err instanceof Error ? err.message : String(err)})`
    );
    return;
  }
  const newGroups = groupRoutesByServer(material.routes);
  const newByKey = new Map(newGroups.map((g) => [g.serverKey, g] as const));
  const oldKeys = new Set(servers.map((s) => s.group.serverKey));
  const newKeys = new Set(newByKey.keys());

  // Warn on add/remove — v1 requires restart for topology changes.
  const added = [...newKeys].filter((k) => !oldKeys.has(k));
  const removed = [...oldKeys].filter((k) => !newKeys.has(k));
  if (added.length > 0) {
    logger.warn(
      `Reload detected new API surface(s): ${added.join(', ')}. Restart '${getEmbedConfig().cliName} start-api' to serve them.`
    );
  }
  if (removed.length > 0) {
    logger.warn(
      `Reload detected removed API surface(s): ${removed.join(', ')}. Their servers will keep serving stale routes until restart.`
    );
  }

  // Per-server: filter material → build pool → swap state → dispose old.
  for (const booted of servers) {
    const group = newByKey.get(booted.group.serverKey);
    if (!group) continue; // removed — skip swap, server keeps stale state until restart
    const groupSpecs = filterSpecsForGroup(group, material.specs);
    const newPool = buildPool(groupSpecs);
    const newState: ServerState = {
      routes: group.routes,
      pool: newPool,
      corsConfigByApiId: material.corsConfigByApiId,
    };
    const previousState = booted.server.setServerState(newState);
    // Update the BootedApiServer's `group` in place so post-reload
    // `printPerServerRouteTables(servers)` reads the new routes,
    // including any new mockCors / unsupported classifications. Pre-fix
    // the printed table always reflected the boot-time routes.
    booted.group = group;
    // Dispose the previous pool in the background. `pool.dispose()`
    // waits for in-flight requests to drain (30s per-entry cap).
    void previousState.pool.dispose().catch((err) => {
      logger.debug(
        `Previous pool dispose() failed for ${group.displayName}: ${err instanceof Error ? err.message : String(err)}`
      );
    });
  }

  // Re-print the per-server route table when any routes changed.
  // Cheap heuristic: always re-print after a successful reload — the
  // user is watching for the diff and a stable table reassures them
  // that the swap landed. `booted.group` was mutated above so this
  // reflects the post-swap routes (including any new mockCors /
  // unsupported classifications introduced mid-edit).
  printPerServerRouteTables(servers);
  const allRoutes = servers.flatMap((s) => s.group.routes.map((r) => r.route));
  warnUnsupportedRoutes(allRoutes, logger);
  warnSsrfRiskyIntegrations(allRoutes, logger);
}

/**
 * Per-stack `--from-state` substitution input consumed by
 * {@link buildContainerSpec}. Built once per `synthesizeAndBuild` pass
 * — initial boot AND every hot-reload firing — by
 * {@link loadStateForRoutedStacks}. Empty (no stack-level entry) when
 * the stack's state could not be loaded or the user did not pass
 * `--from-state`.
 */
export interface StackStateBundle {
  state: StackState;
  /**
   * AWS pseudo parameters (account / region / partition / URL suffix).
   * `undefined` when none of the stack's reachable Lambdas has a
   * pseudo-parameter intrinsic in its env map (skips the STS hop) OR
   * the STS resolution failed (substitution still runs for non-`AWS::*`
   * refs).
   */
  pseudoParameters?: PseudoParameters;
  /**
   * Deploy-time-resolved `Environment.Variables` per reachable Lambda
   * logical ID, fetched while the `--from-cfn-stack` provider was alive.
   * Consumed by `buildContainerSpec` to fill env keys whose intrinsic
   * value the static substituter could not resolve (e.g.
   * `Fn::GetAtt <Sibling>.Arn`). `undefined`/absent for stacks where no
   * provider implements the deployed-env fallback (e.g. `--from-state`).
   */
  deployedEnvByLambda?: Map<string, Record<string, string>>;
  /**
   * Resolved SSM-backed template `Parameters`
   * (`AWS::SSM::Parameter::Value<String>`), keyed by the parameter's
   * logical ID. Fed into the substitution context's `parameters` field so
   * a `Ref` to such a parameter resolves to its SSM value instead of
   * being warn-and-dropped (issue #94). `undefined`/absent when the stack
   * declares no SSM-backed parameters or no provider implements the SSM
   * resolution (e.g. `--from-state`).
   */
  ssmParameters?: Record<string, string>;
  /**
   * Logical IDs of the resolved SSM parameters whose `Type` is
   * `SecureString` (decrypted). Threaded into the substitution context's
   * `sensitiveParameters` so the consuming env keys are routed off the
   * `docker run` argv (issue #99). `undefined`/absent when none.
   */
  ssmSecureStringLogicalIds?: string[];
}

/**
 * Returns true when any value in the function's template env map is a
 * CFn intrinsic (non-primitive). Used to gate the pseudo-parameter STS
 * hop inside the `--from-state` flow: literal-only env maps don't need
 * the pseudo-parameter bag and shouldn't pay for an STS call. Mirrors
 * the same gating in `local-invoke.ts` (`envHasIntrinsicValue`) and
 * `ecs-task-resolver.ts` (`containerHasIntrinsicEnvOrSecret`).
 */
export function envHasIntrinsicValue(templateEnv: Record<string, unknown> | undefined): boolean {
  if (!templateEnv) return false;
  for (const v of Object.values(templateEnv)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') continue;
    return true;
  }
  return false;
}

/**
 * Load deployed state for every stack that owns a routed Lambda. Once
 * per `synthesizeAndBuild` pass (initial boot + every reload), so a
 * Lambda's per-spec build does not pay one round-trip per Lambda. Per-
 * stack failures (no state, ambiguous region, bucket resolution error)
 * degrade to warn-and-fall-back via the active `LocalStateProvider` —
 * the affected stack's reachable Lambdas behave as if `--from-state` /
 * `--from-cfn-stack` were not set, while sibling stacks with loadable
 * state still substitute.
 *
 * Pseudo parameters are resolved per stack and only when at least one
 * reachable Lambda in that stack has an intrinsic-valued env entry
 * (gated via {@link envHasIntrinsicValue}). STS failures degrade to
 * warn and leave `pseudoParameters: undefined` — substitution still
 * runs for non-`AWS::*` refs.
 */
/**
 * Returns true when at least one host-provided extra state-provider flag
 * is active in the options bag.
 */
function hasExtraStateProviderActive(
  options: LocalStartApiOptions,
  extraStateProviders: ExtraStateProviders | undefined
): boolean {
  if (!extraStateProviders) return false;
  for (const key of Object.keys(extraStateProviders)) {
    if ((options as unknown as Record<string, unknown>)[key]) return true;
  }
  return false;
}

export async function loadStateForRoutedStacks(
  stacks: readonly StackInfo[],
  routes: readonly DiscoveredRoute[],
  routesWithAuth: readonly RouteWithAuth[],
  options: LocalStartApiOptions,
  extraStateProviders: ExtraStateProviders | undefined
): Promise<Map<string, StackStateBundle>> {
  const logger = getLogger();
  const out = new Map<string, StackStateBundle>();

  // Collect the set of (stackName, region) pairs that contain at least
  // one routed Lambda OR an attached authorizer Lambda. The stack
  // resolution mirrors `resolveLambdaByLogicalId`'s walk so we never
  // load state for a stack whose Lambdas are not actually reachable
  // from the discovered API surface.
  const lambdaIds = uniqueLambdaIds(routes, routesWithAuth);
  const reachableStackNames = new Set<string>();
  for (const logicalId of lambdaIds) {
    for (const stack of stacks) {
      const resource = stack.template.Resources?.[logicalId];
      if (resource && resource.Type === 'AWS::Lambda::Function') {
        reachableStackNames.add(stack.stackName);
        break;
      }
    }
  }

  // Pre-compute "does any reachable Lambda in this stack have an
  // intrinsic-valued env map" — gates the per-stack STS hop.
  const stackHasIntrinsicEnv = (stackName: string): boolean => {
    for (const logicalId of lambdaIds) {
      for (const stack of stacks) {
        if (stack.stackName !== stackName) continue;
        const resource = stack.template.Resources?.[logicalId];
        if (!resource || resource.Type !== 'AWS::Lambda::Function') continue;
        if (envHasIntrinsicValue(getTemplateEnv(resource))) return true;
      }
    }
    return false;
  };

  // Issue #606: route through `createLocalStateProvider` so the same
  // code path drives both `--from-state` (S3) and `--from-cfn-stack`
  // (CFn). One provider PER reachable stack — bare `--from-cfn-stack`
  // uses the host stack name per routed stack, so the dispatcher needs
  // each stack's name at construction time. Each provider is disposed
  // after its `load` call returns so the AWS client tied to that
  // provider doesn't outlive the boot pass.
  //
  // Reject explicit `--from-cfn-stack <name>` when multiple host stacks
  // are routed: the explicit name would apply to every routed stack
  // and silently misresolve `Ref` lookups when logical IDs happen to
  // collide between siblings (see local-state-source.ts for the
  // rationale). Bare `--from-cfn-stack` is safe because each routed
  // stack uses its own host stack name.
  rejectExplicitCfnStackWithMultipleStacks(options, reachableStackNames.size);
  for (const stackName of reachableStackNames) {
    const stack = stacks.find((s) => s.stackName === stackName);
    if (!stack) continue;
    const provider = createLocalStateProvider(
      options,
      stack.stackName,
      await resolveCfnFallbackRegion(options, stack.region),
      extraStateProviders
    );
    if (!provider) continue;
    try {
      const loaded = await provider.load(stack.stackName, stack.region);
      if (!loaded) continue;

      // Synthesize a `StackState` shape from the provider's
      // `LocalStateRecord` so the downstream `buildContainerSpec` path
      // (which reads `stateBundle.state.resources`) keeps working
      // verbatim. Outputs is an unused field at this call site but
      // populated for completeness; the StackState `version` /
      // `lastModified` carry placeholder values — the
      // sync `substituteEnvVarsFromState` resolver only reads
      // `resources`.
      const syntheticState: StackState = {
        version: 1,
        stackName: stack.stackName,
        resources: loaded.resources,
        outputs: loaded.outputs,
        lastModified: 0,
      };
      const bundle: StackStateBundle = { state: syntheticState };
      if (stackHasIntrinsicEnv(stackName)) {
        const pseudo = await resolvePseudoParametersForStartApi(loaded.region, options);
        if (pseudo) bundle.pseudoParameters = pseudo;
      }
      // Deployed-env fallback source: for each reachable Lambda in this
      // stack whose template env carries an intrinsic, fetch the deployed
      // function's already-resolved `Environment.Variables` while the
      // provider (and its AWS client) is still alive. `buildContainerSpec`
      // later splices these in for keys static substitution dropped (e.g.
      // `Fn::GetAtt <Sibling>.Arn`). Only the CFn provider implements
      // `resolveDeployedFunctionEnv`; `--from-state` carries deploy-time
      // attributes in its state so its GetAtt resolves statically.
      if (provider.resolveDeployedFunctionEnv) {
        const deployedEnvByLambda = new Map<string, Record<string, string>>();
        for (const logicalId of lambdaIds) {
          const resource = stack.template.Resources?.[logicalId];
          if (!resource || resource.Type !== 'AWS::Lambda::Function') continue;
          if (!envHasIntrinsicValue(getTemplateEnv(resource))) continue;
          const physicalId = loaded.resources[logicalId]?.physicalId;
          if (!physicalId) continue;
          const deployedEnv = await provider.resolveDeployedFunctionEnv(physicalId);
          if (deployedEnv) deployedEnvByLambda.set(logicalId, deployedEnv);
        }
        if (deployedEnvByLambda.size > 0) bundle.deployedEnvByLambda = deployedEnvByLambda;
      }
      // Resolve SSM-backed template parameters
      // (`AWS::SSM::Parameter::Value<String>`) once per stack so a `Ref`
      // to such a parameter in a reachable Lambda's env resolves to its
      // SSM value (issue #94). Gated on the stack actually having an
      // intrinsic-valued env entry (same gate as the pseudo-parameter
      // hop) so literal-only stacks skip the SSM round-trip. Only the
      // CFn provider implements `resolveTemplateSsmParameters`.
      if (stackHasIntrinsicEnv(stackName) && provider.resolveTemplateSsmParameters) {
        const ssmParameters = await provider.resolveTemplateSsmParameters(stack.template);
        if (Object.keys(ssmParameters.values).length > 0) {
          bundle.ssmParameters = ssmParameters.values;
        }
        if (ssmParameters.secureStringLogicalIds.length > 0) {
          bundle.ssmSecureStringLogicalIds = ssmParameters.secureStringLogicalIds;
        }
      }
      out.set(stackName, bundle);
      logger.debug(`${provider.label}: loaded state for ${stackName} (${loaded.region})`);
    } finally {
      provider.dispose();
    }
  }
  return out;
}

/**
 * Build the AWS pseudo-parameter bag for `--from-state` env-var
 * substitution. Mirrors `resolvePseudoParametersForInvoke` in
 * `local-invoke.ts` byte-for-byte — kept inlined here rather than
 * extracted into a shared helper because the two call sites differ in
 * region precedence (this one is per-stack so the resolved state
 * region takes priority).
 *
 * Region precedence: `--region` > `AWS_REGION` > `AWS_DEFAULT_REGION` >
 * the state record's region (returned by the active `LocalStateProvider`).
 *
 * Exported so the issue #570 site-level test can drive the STS failure
 * branch with a mocked STS client. A helper being correct says nothing about
 * whether a given site actually routes through it, so each of the nine
 * converted sites is driven separately.
 */
export async function resolvePseudoParametersForStartApi(
  stateRegion: string,
  options: LocalStartApiOptions
): Promise<PseudoParameters | undefined> {
  const logger = getLogger();
  const region =
    options.region ?? process.env['AWS_REGION'] ?? process.env['AWS_DEFAULT_REGION'] ?? stateRegion;
  let accountId: string | undefined;
  try {
    const { STSClient, GetCallerIdentityCommand } = await import('@aws-sdk/client-sts');
    // Thread `--profile` so the resolved account is the profile's account,
    // not whatever the default credential chain points at (issue #245).
    const sts = new STSClient(buildStsClientConfig({ region, profile: options.profile }));
    try {
      const identity = await sts.send(new GetCallerIdentityCommand({}));
      accountId = identity.Account;
    } finally {
      sts.destroy();
    }
  } catch (err) {
    // Issue #570: never interpolate the SDK error's `message` into this
    // default-level line -- see `describeAwsFailureForWarn` in
    // `src/local/credential-error.ts` for which half of it prints here and
    // which is withheld to `debug`.
    logger.warn(
      `--from-state: resolver needs \${AWS::AccountId} but STS GetCallerIdentity failed: ${describeAwsFailureForWarn(err, 'STS GetCallerIdentity')}. ` +
        'Substitution will be skipped for AWS::AccountId; affected env entries will be dropped with per-key warnings.'
    );
  }
  const partitionAndSuffix = region ? derivePartitionAndUrlSuffix(region) : undefined;
  const bag: PseudoParameters = {
    ...(accountId !== undefined && { accountId }),
    ...(region !== undefined && { region }),
    ...(partitionAndSuffix && {
      partition: partitionAndSuffix.partition,
      urlSuffix: partitionAndSuffix.urlSuffix,
    }),
  };
  return Object.keys(bag).length === 0 ? undefined : bag;
}

/** Validate `--debug-port-base`. */
function parseDebugPort(raw: string): number {
  const parsed = parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 65535) {
    throw new Error(`--debug-port-base must be 1..65535 (got '${raw}')`);
  }
  return parsed;
}

/**
 * Resolve the mTLS configuration from CLI options. Returns `undefined`
 * when none of the three `--mtls-*` flags is set (the server stays
 * plain-HTTP). When any of the three is set, ALL THREE must be set —
 * partial configurations are rejected at parse time so the server
 * never boots in a half-configured state.
 *
 * Exported for unit testing.
 */
export function resolveMtlsConfig(
  options: Pick<LocalStartApiOptions, 'mtlsTruststore' | 'mtlsCert' | 'mtlsKey'>
): MtlsServerConfig | undefined {
  const present: string[] = [];
  const absent: string[] = [];
  if (options.mtlsTruststore !== undefined && options.mtlsTruststore !== '') {
    present.push('--mtls-truststore');
  } else {
    absent.push('--mtls-truststore');
  }
  if (options.mtlsCert !== undefined && options.mtlsCert !== '') {
    present.push('--mtls-cert');
  } else {
    absent.push('--mtls-cert');
  }
  if (options.mtlsKey !== undefined && options.mtlsKey !== '') {
    present.push('--mtls-key');
  } else {
    absent.push('--mtls-key');
  }
  if (present.length === 0) return undefined;
  if (absent.length > 0) {
    throw new Error(
      `mTLS configuration is incomplete: ${present.join(', ')} set but ${absent.join(', ')} missing. ` +
        'All three of --mtls-truststore, --mtls-cert, and --mtls-key must be set together to enable mTLS, ' +
        'or all three left unset for plain HTTP.'
    );
  }
  // All three set — read the PEM materials from disk. Failures surface
  // a clear error naming the offending flag + path before the server
  // starts.
  return readMtlsMaterialsFromDisk({
    truststorePath: options.mtlsTruststore!,
    certPath: options.mtlsCert!,
    keyPath: options.mtlsKey!,
  });
}

/**
 * Builder for the `start-api` subcommand.
 */
export function createLocalStartApiCommand(opts: CreateLocalStartApiCommandOptions = {}): Command {
  setEmbedConfig(opts.embedConfig);
  const startApi = new Command('start-api')
    .description(
      'Run a long-running local HTTP server that maps API Gateway routes (REST v1, HTTP API, Function URL) to Lambda invocations against the AWS Lambda Runtime Interface Emulator (Docker required). Supports Lambda TOKEN/REQUEST authorizers, Cognito User Pool / HTTP v2 JWT authorizers, and AWS_IAM auth (REST v1 `AuthorizationType: AWS_IAM` and Function URL `AuthType: AWS_IAM` — SigV4 signature verification only; IAM policy evaluation is NOT emulated). When JWKS is unreachable, JWT authorizers fall back to pass-through (every token accepted) with a warn line — local dev fallback. VPC-config Lambdas run locally and surface a warn line at startup; their containers do NOT get attached to the deployed VPC subnets, so calls to private RDS / ElastiCache will fail.'
    )
    .argument(
      '[targets...]',
      `Optional API subset filter. Pass one or more identifiers to serve exactly that subset (the union; each on its own port). Each accepts the bare CDK logical id ('MyHttpApi'; single-stack apps only), stack-qualified logical id ('MyStack:MyHttpApi'), full CDK Construct path ('MyStack/MyHttpApi/Resource'), or an ancestor Construct path that prefix-matches ('MyStack/MyHttpApi'). When omitted in a TTY, a multi-select picker opens with every API pre-selected (Enter serves all, deselect to pick a subset); when omitted in a non-TTY (CI / pipe) every discovered API is served. Mirrors \`${getEmbedConfig().cliName} invoke\` / \`${getEmbedConfig().cliName} run-task\` target syntax.`
    )
    .action(
      withErrorHandling(async (targets: string[], options: LocalStartApiOptions) => {
        await localStartApiCommand(targets, options, opts.extraStateProviders);
      })
    );

  addStartApiSpecificOptions(startApi);
  [...commonOptions(), ...appOptions(), ...contextOptions].forEach((opt) =>
    startApi.addOption(opt)
  );
  startApi.addOption(regionOption);

  return startApi;
}

/**
 * Register the option block that `cdkl start-api` adds on top of the shared
 * common / app / context option helpers. Shared between `cdkl start-api`
 * and any host CLI (e.g. cdkd's `local start-api`) that wraps the
 * long-running API-Gateway-fronted local HTTP server, so adding or
 * renaming a `start-api`-only flag here propagates to every embedder
 * without duplicate `.addOption(...)` blocks.
 *
 * Calling order only affects `--help` presentation (Commander parses
 * insertion-order-independent). The host-CLI convention is host-specific
 * options first, then this helper, then the shared common / app / context
 * options — host flags / start-api flags / common flags grouped in three
 * `--help` clusters. Chainable: returns `cmd`.
 */
export function addStartApiSpecificOptions(cmd: Command): Command {
  return (
    cmd
      .addOption(
        new Option('--port <port>', 'HTTP server port (default: auto-allocate)').default('0')
      )
      .addOption(new Option('--host <host>', 'Bind address').default('127.0.0.1'))
      .addOption(new Option('--stack <name>', 'Stack to start (single-stack apps auto-detect)'))
      .addOption(
        new Option(
          '--all-stacks',
          "Serve every stack's API in a multi-stack app (each API on its own port) instead of erroring out. Mutually exclusive with a positional target subset, --stack, and an explicit --from-cfn-stack <name>; the bare --from-cfn-stack flag stays compatible (binds each routed stack to its own CFn stack)."
        ).default(false)
      )
      .addOption(
        new Option('--warm', 'Pre-start one container per Lambda at server boot').default(false)
      )
      .addOption(
        new Option(
          '--per-lambda-concurrency <n>',
          'Pool size cap per Lambda (default 2, max 4)'
        ).default('2')
      )
      .addOption(new Option('--no-pull', 'Skip docker pull (cached image)'))
      .addOption(
        new Option(
          '--no-build',
          'Skip docker build on every container Lambda local-asset build (use the previously-built tag). ' +
            'Requires the deterministic tag to already be in the local registry; errors with an ' +
            'actionable message when missing. No-op for ZIP Lambdas and the IMAGE ECR-pull path. ' +
            'Compatible with --no-pull.'
        )
      )
      .addOption(
        new Option(
          '--container-host <host>',
          'IP the host uses to bind/probe the RIE port (must be a numeric IP — `docker run -p <ip>:<port>:8080` rejects hostnames). Defaults to 127.0.0.1.'
        ).default('127.0.0.1')
      )
      .addOption(
        new Option(
          '--debug-port-base <port>',
          'Reserve a contiguous --debug-port range (one per Lambda)'
        )
      )
      // Issue #249 / C14 — `--debug-port` alias matching `cdkl invoke`'s
      // flag name. Both forms accepted; `--debug-port-base` stays the
      // canonical name (its meaning — RESERVE A CONTIGUOUS RANGE rather
      // than a single port — is what differs from `cdkl invoke`). When
      // both are passed Commander stores the later one; we coalesce in
      // `localStartApiCommand`.
      .addOption(
        new Option(
          '--debug-port <port>',
          'Alias of --debug-port-base for parity with `cdkl invoke --debug-port`. ' +
            'Reserves a contiguous --debug-port range (one per Lambda) starting at <port>.'
        )
      )
      .addOption(
        new Option(
          '--ecr-role-arn <arn>',
          'Role ARN to assume before authenticating against ECR for cross-account / centralized ' +
            'registries on the container-Lambda ECR-pull fallback. Issues sts:AssumeRole via the ' +
            'default credential chain and uses the temporary credentials for ' +
            'ecr:GetAuthorizationToken + docker pull. Required when the caller does not have direct ' +
            'cross-account access to the target repository. Same-account / same-region pulls do not ' +
            'need this flag.'
        )
      )
      .addOption(
        new Option(
          '--env-vars <file>',
          'JSON env-var overrides (SAM-compatible: {"LogicalId":{"KEY":"VALUE"}, "Parameters": {...}})'
        )
      )
      .addOption(
        new Option(
          '--assume-role <arn-or-pair>',
          "Assume the Lambda's execution role and forward STS-issued temp creds. Two forms: " +
            '(1) `--assume-role <arn>` sets a single global default ARN used for every routed Lambda; ' +
            '(2) `--assume-role <LogicalId>=<arn>` (repeatable) is a per-Lambda override (wins over the global default and over --assume-role-auto for the named Lambda). ' +
            'Pair with --assume-role-auto to auto-resolve every other Lambda. Precedence: per-Lambda > (--assume-role-auto OR global default) > unset (developer creds passed through).'
        ).argParser((raw, prev: AssumeRoleOption | undefined) => parseAssumeRoleToken(raw, prev))
      )
      .addOption(
        new Option(
          '--assume-role-auto',
          "Auto-resolve EACH routed Lambda's own execution role per-Lambda (literal ARN / Fn::GetAtt against template / --from-cfn-stack state). " +
            "Slower boot — one STS call per Lambda — but the right shape when each Lambda's deployed role differs. " +
            'Mutually exclusive with `--assume-role <arn>` (global default); errors at boot. ' +
            'Compatible with `--assume-role <LogicalId>=<arn>` per-Lambda overrides — the map wins for named Lambdas, auto-resolve handles the rest.'
        ).default(false)
      )
      .addOption(
        new Option(
          '--watch',
          "Hot-reload: re-synth + re-discover routes when the CDK app's source changes (honors cdk.json watch.include/exclude; cdk.out, node_modules, .git are always excluded). Off by default; the server keeps the previous version serving when synth fails mid-reload."
        ).default(false)
      )
      .addOption(
        new Option(
          '--stage <name>',
          "Select an API Gateway Stage by its 'StageName'. Default: the first Stage attached to each API. Drives event.stageVariables for both REST v1 and HTTP API v2. NOTE: For HTTP API v2 routes, requestContext.stage is always '$default' regardless of this flag (AWS-side limitation — HTTP API only exposes one stage to the integration event); only event.stageVariables is affected for v2 routes. For REST v1 routes the selected StageName is also threaded into requestContext.stage."
        )
      )
      .addOption(
        new Option(
          '--api <id>',
          'DEPRECATED — use the positional <targets...> argument instead. Accepts a SINGLE identifier; for a subset pass multiple positional targets. Same accepted forms (bare logical id, stack-qualified, Construct path, ancestor prefix). Will be removed in a future major release.'
        )
      )
      .addOption(
        new Option(
          '--layer-role-arn <arn>',
          'Role to sts:AssumeRole before calling lambda:GetLayerVersion on every literal-ARN ' +
            'entry in Properties.Layers (issue #448). Use only when the dev credentials cannot ' +
            'read the layer — typically cross-account layers. AWS-published public layers (e.g. ' +
            'Lambda Powertools) are readable from every account and need no role.'
        )
      )
      .addOption(
        new Option(
          '--from-cfn-stack [cfn-stack-name]',
          'Read a deployed CloudFormation stack via ListStackResources and substitute Ref / Fn::ImportValue ' +
            'in Lambda env vars with the deployed physical IDs / exports. ' +
            'Use for CDK apps deployed via the upstream CDK CLI (`cdk deploy`). ' +
            'Bare form uses the resolved stack name per routed stack; pass an explicit value when a single CFn stack should serve every routed stack. ' +
            'Fn::GetAtt is warn-and-dropped in v1 (CFn ListStackResources does not return per-attribute values).'
        )
      )
      .addOption(
        new Option(
          '--stack-region <region>',
          'Region of the state record to read. Used with --from-cfn-stack as the CFn client region.'
        )
      )
      .addOption(
        new Option(
          '--mtls-truststore <path>',
          'PEM-encoded CA bundle for client-certificate verification (mutual TLS). ' +
            'When set, the local server switches from HTTP to HTTPS and the TLS handshake rejects ' +
            "clients whose certificate doesn't chain to one of these CAs. Verified certs are surfaced " +
            'on the Lambda event under requestContext.identity.clientCert (REST v1) / ' +
            'requestContext.authentication.clientCert (HTTP API v2). Must be set together with ' +
            '--mtls-cert + --mtls-key; partial flag sets are rejected. ' +
            'Generate a CA + server + client cert for local dev: ' +
            `openssl req -x509 -newkey rsa:2048 -nodes -keyout ca-key.pem -out ca.pem -subj "/CN=${getEmbedConfig().resourceNamePrefix}-ca" -days 365; ` +
            'openssl req -newkey rsa:2048 -nodes -keyout server-key.pem -out server-csr.pem -subj "/CN=localhost"; ' +
            'openssl x509 -req -in server-csr.pem -CA ca.pem -CAkey ca-key.pem -CAcreateserial -out server-cert.pem -days 365; ' +
            'openssl req -newkey rsa:2048 -nodes -keyout client-key.pem -out client-csr.pem -subj "/CN=client"; ' +
            'openssl x509 -req -in client-csr.pem -CA ca.pem -CAkey ca-key.pem -CAcreateserial -out client-cert.pem -days 365; ' +
            'curl --cacert ca.pem --cert client-cert.pem --key client-key.pem https://localhost:<port>/...'
        )
      )
      .addOption(
        new Option(
          '--mtls-cert <path>',
          'PEM-encoded server certificate for mutual TLS. Self-signed is fine for local dev. ' +
            'Must be set together with --mtls-truststore + --mtls-key.'
        )
      )
      .addOption(
        new Option(
          '--mtls-key <path>',
          'PEM-encoded server private key matching --mtls-cert. ' +
            'Must be set together with --mtls-truststore + --mtls-cert.'
        )
      )
      .addOption(
        new Option(
          '--strict-sigv4',
          'Opt-in: DENY AWS_IAM SigV4 requests that cannot be cryptographically verified ' +
            '(foreign access-key-id — e.g. a federated / Cognito Identity Pool / cross-account ' +
            'signer — OR no local AWS credentials configured) instead of the default warn-and-pass. ' +
            'DEFAULT off: cdk-local warn-and-passes unverifiable IAM requests with a placeholder ' +
            'principalId so local dev exercises app logic without reproducing an auth boundary it ' +
            'cannot fully emulate. OAC-fronted Function URLs always warn-and-pass regardless.'
        ).default(false)
      )
  );
}
